import React, { useState, useEffect } from 'react';
import { AssetServicer } from '../components/servicers/ServicerFields';
import { AssetShareButton } from '../components/AssetShareButton';
import { AssetAssignments } from '../components/AssetAssignments';
import { canViewAssetSupplier, canManageAssignments } from '../lib/assetPermissions';
import { FASE_JUDICIAL_LABELS, FASE_JUDICIAL_LIST } from '../constants/faseJudicial';
import { InlineTextField } from '../components/InlineTextField';
import { JudicialDataCard } from '../components/JudicialDataCard';
import { AssetExclusionPanel } from '../components/geografia/AssetExclusionPanel';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Building2, MapPin, Edit2, Save, Star, 
  FileText, Users, Phone, MessageCircle, FileUp, Sparkles, Trash2, 
  Download, Plus, Loader2, Calculator, Pencil, FolderOpen, ScanLine, Check, X as XIcon, Receipt,
  Send, Eye, ExternalLink, Search, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ROIAdvancedTab from '../components/ROIAdvancedTab';
import BalanceTab from '../components/BalanceTab';
import ValoracionSalidaTab from '../components/ValoracionSalidaTab';
import RentalAnalysisSection from '../components/RentalAnalysisSection';
import CreditoScoringTab from '../components/CreditoScoringTab';
import RequestReviewModal from '../components/RequestReviewModal';
import ResolveReviewModal from '../components/ResolveReviewModal';
import ResolveOrPostponeChoiceModal from '../components/ResolveOrPostponeChoiceModal';
import { ReviewPostponeDialog } from '../components/ReviewPostponeDialog';
import MediacionAssignModal from '../components/MediacionAssignModal';
import TareaGestionModal from '../components/TareaGestionModal';
import TasksSection from '../components/TasksSection';
import ConfirmMediacionActivaModal from '../components/ConfirmMediacionActivaModal';
import ClosureReasonModal from '../components/ClosureReasonModal';
import {
  RoiValue,
  hasRealRoiVenta,
  getRoiVentaValue,
  hasRealRoiAlquiler,
  getRoiAlquilerValue,
} from '../components/RoiDisplay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Lookup tables for inline-edit chips in the header.
// Pipeline redesign (Feb 2026): the asset lifecycle is split in three layers:
//   - estado:      operational decision (5 values)
//   - fase:        real investment pipeline (6 values)
//   - etapa_visual: derived view for listings (read-only, computed by backend)
const ESTADO_OPTIONS = [
  { v: 'activo', l: 'Activo' },
  { v: 'standby', l: 'Standby' },
  { v: 'cerrado', l: 'Cerrado' },
  { v: 'papelera', l: 'Papelera' },
];
// Tooltips descriptivos (spec Feb 2026 §2 y §3).
const TOOLTIP_ESTADO = 'Situación de la oportunidad dentro de XÉNICOS';
const TOOLTIP_ETAPA = 'Punto del proceso de adquisición en el que se encuentra';
const FASE_LABELS = {
  nuevo: 'Nuevo',
  revision: 'Revisión',
  analisis: 'Análisis',
  mediacion: 'Mediación',
  validacion: 'Validación',
  cierre: 'Cierre',
};
const FASE_OPTIONS = Object.keys(FASE_LABELS).map(v => ({ v, l: FASE_LABELS[v] }));
const ETAPA_VISUAL_LABELS = {
  nuevo:          'Nuevo',
  estudio:        'Estudio',
  mediacion:      'Mediación',
  en_gestion:     'En Gestión',
  oferta:         'Oferta',
  proceso_compra: 'Proceso compra',
  adquirido:      'Adquirido',
};
const ETAPA_VISUAL_OPTIONS = Object.keys(ETAPA_VISUAL_LABELS).map(v => ({ v, l: ETAPA_VISUAL_LABELS[v] }));
const ETAPA_VISUAL_BADGES = {
  nuevo:          'bg-sky-100 text-sky-700 border-sky-200',
  estudio:        'bg-amber-100 text-amber-700 border-amber-200',
  mediacion:      'bg-violet-100 text-violet-700 border-violet-200',
  en_gestion:     'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  oferta:         'bg-blue-100 text-blue-700 border-blue-200',
  proceso_compra: 'bg-orange-100 text-orange-700 border-orange-200',
  adquirido:      'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const PRIORIDAD_OPTIONS = [
  { v: 'urgente', l: 'Urgente' },
  { v: 'alta', l: 'Alta' },
  { v: 'normal', l: 'Normal' },
  { v: 'baja', l: 'Baja' },
  { v: 'no_prioritario', l: 'No prioritario' },
];

// Reusable inline-edit chip — same UX as the listing page (click → native
// <select> auto-saves via PATCH /assets/{id}/quick-update). Used in the
// header of the asset detail page for estado / fase / fase_judicial / prioridad.
const HeaderInlineChip = ({ assetId, field, value, options, badgeClass, placeholder, onSaved, testid, disabled, tooltip, disabledTitle, onDisabledClick, beforeSave }) => {
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const labelOf = (v) => (options.find(o => o.v === v) || {}).l || (v ? String(v).replace(/_/g, ' ') : placeholder);
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  if (disabled) {
    // Si hay handler para el click en modo deshabilitado (ej. Mediador
    // solicitando revisión al Gestor/Admin), renderizamos un <button>
    // clicable en vez de un <span> estático.
    if (typeof onDisabledClick === 'function') {
      return (
        <button
          type="button"
          onClick={(e) => { stop(e); onDisabledClick(); }}
          className={`text-xs px-2.5 py-1 rounded-full font-medium border cursor-pointer hover:shadow-sm hover:opacity-90 transition-shadow ${badgeClass}`}
          title={disabledTitle || tooltip || undefined}
          data-testid={testid}
        >
          {labelOf(value)}
        </button>
      );
    }
    return <span className={`text-xs px-2.5 py-1 rounded-full font-medium border opacity-70 cursor-not-allowed ${badgeClass}`} title={disabledTitle || tooltip || undefined} data-testid={testid}>{labelOf(value)}</span>;
  }
  if (editing) {
    return (
      <select
        autoFocus
        defaultValue={value || ''}
        disabled={saving}
        onClick={stop}
        onChange={async (e) => {
          const v = e.target.value;
          // Interceptor opcional: si retorna true, el chip NO ejecuta el
          // quick-update — el padre asume la persistencia (por ejemplo,
          // modal de asignación de mediador antes de mover a "mediacion").
          if (typeof beforeSave === 'function') {
            try {
              const intercepted = await beforeSave(v);
              if (intercepted) { setEditing(false); return; }
            } catch { /* si falla el interceptor seguimos con save normal */ }
          }
          setSaving(true);
          try {
            const r = await axios.patch(`${API}/assets/${assetId}/quick-update`, { [field]: v || null }, { withCredentials: true });
            toast.success('Cambio guardado');
            setEditing(false);
            if (onSaved) onSaved(r.data?.asset || null);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
          } finally { setSaving(false); }
        }}
        onBlur={() => { if (!saving) setEditing(false); }}
        className="text-xs rounded border px-1.5 py-0.5"
        style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)', minWidth: 130 }}
        data-testid={`${testid}-select`}
      >
        <option value="">— sin valor —</option>
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { stop(e); setEditing(true); }}
      className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-shadow hover:shadow-sm hover:opacity-90 cursor-pointer ${badgeClass}`}
      title={tooltip || 'Clic para editar'}
      data-testid={testid}
    >
      {labelOf(value)}
    </button>
  );
};

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');

  // Deep-link a una pestaña concreta (p.ej. desde "Mis tareas" → ?tab=tareas).
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) setActiveTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sub-module states
  const [notes, setNotes] = useState([]);
  const [mediation, setMediation] = useState(null);
  const [calls, setCalls] = useState([]);
  const [whatsapp, setWhatsapp] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [valoracionesIA, setValoracionesIA] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskAssignableUsers, setTaskAssignableUsers] = useState([]);

  // Publish state
  const [showPublish, setShowPublish] = useState(false);
  const [publishTipo, setPublishTipo] = useState('actualizacion');
  const [publishMsg, setPublishMsg] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [catastroLoading, setCatastroLoading] = useState(false);

  // Role-based access (Roles v2)
  const userRole = typeof currentUser?.role === 'string' ? currentUser.role.trim().toLowerCase() : '';
  const isSuperadmin = userRole === 'superadmin';
  const isInversor = userRole === 'inversor';
  const isMediador = userRole === 'mediador';
  // ID Servicer / ID Finca: solo Admin y Superadmin pueden editarlos manualmente.
  const canEditAdminOnlyFields = isSuperadmin || userRole === 'admin';
  const canViewResumen = canViewAssetSupplier(currentUser, asset);
  // Nunca se infiere el rol de usuario a partir de una asignación al activo.
  const canEdit = canViewResumen;
  useEffect(() => {
    if (!currentUser || !asset) return;
    if (isInversor && !['roi', 'tareas', 'documentos', 'balance'].includes(activeTab)) {
      setActiveTab('roi');
    } else if (!canViewResumen && activeTab === 'resumen') {
      setActiveTab('datos');
    }
  }, [currentUser, asset, isInversor, canViewResumen, activeTab]);

  // AI Valuation state
  const [aiInput, setAiInput] = useState({
    referencia_catastral: '',
    municipio: '',
    metros: 0,
    tipo_activo: '',
    estado: ''
  });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Modal "Solicitar revisión" (destinatarios + nota obligatoria).
  const [showReviewModal, setShowReviewModal] = useState(false);
  // (Feb 2026) Modal para EDITAR la solicitud de revisión activa. Solo el
  // creador de la solicitud lo puede abrir; carga los datos actuales
  // (destinatarios, nota, prioridad) para precargar el formulario.
  const [showEditReviewModal, setShowEditReviewModal] = useState(false);
  const [editReviewInitial, setEditReviewInitial] = useState(null);
  // Mediador: al pulsar Estado/Fase (deshabilitados), mostramos un modal
  // informativo que sugiere solicitar la revisión al Gestor/Admin.
  const [mediadorReadOnlyField, setMediadorReadOnlyField] = useState(null);
  // Modal de asignación obligatoria al pasar a fase "Mediación" (spec Feb 2026).
  const [mediacionModalOpen, setMediacionModalOpen] = useState(false);
  const [tareaGestionModalOpen, setTareaGestionModalOpen] = useState(false);
  // (Feb 2026 mediacion_activa) Confirm modal al mover a oferta/proceso_compra.
  // Guarda `{ value }` como fase destino pendiente. Al responder Sí/No,
  // se ejecuta el patch con `{ etapa_visual: value, mediacion_activa: bool }`.
  const [confirmMediacionActiva, setConfirmMediacionActiva] = useState(null);
  // (Feb 2026) Modal obligatorio "Motivo de cierre" al pasar el estado
  // a `cerrado` desde el chip del header. Reutiliza el mismo pipeline
  // `descarte_motivos` que la ficha de edición y el bulk.
  const [closureModalOpen, setClosureModalOpen] = useState(false);
  // Modal "Resolver revisión" (nota obligatoria; notifica al solicitante).
  const [showResolveModal, setShowResolveModal] = useState(false);
  // Al pulsar "Desmarcar" se abre primero un panel de elección: Resolver o
  // Posponer. Si elige Posponer, se carga la solicitud activa (audit_log_id
  // + estado de posposición actual) y se abre el diálogo de posponer
  // reutilizando el mismo componente que en "Pendientes de revisión".
  const [showUnmarkChoice, setShowUnmarkChoice] = useState(false);
  const [postponeReviewItem, setPostponeReviewItem] = useState(null);

  const handleChoosePostponeFromAsset = async () => {
    try {
      const r = await axios.get(`${API}/assets/${asset.id}/review-request`, { withCredentials: true });
      if (!r.data?.active) {
        toast.error('No hay una revisión activa que posponer');
        return;
      }
      setShowUnmarkChoice(false);
      setPostponeReviewItem({
        audit_log_id: r.data.audit_log_id,
        postponement: r.data.postponement || null,
        asset_codigo_npl: asset.codigo_npl,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la solicitud');
    }
  };

  useEffect(() => {
    fetchAsset();
  }, [id]);

  useEffect(() => {
    if (asset) {
      fetchNotes(); fetchMediation(); fetchCalls(); fetchWhatsapp(); fetchContacts(); fetchDocuments(); fetchInteractions(); fetchValoracionesIA(); fetchUsers(); fetchTasks(); fetchTaskAssignableUsers();
      setAiInput({
        referencia_catastral: asset.referencia_catastral || '',
        municipio: asset.municipio || '',
        metros: asset.metros || 0,
        tipo_activo: asset.tipo_activo || '',
        estado: asset.estado_procesal || ''
      });
    }
  }, [asset]);

  const fetchAsset = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}`, { withCredentials: true });
      setAsset(response.data);
    } catch (error) {
      toast.error('Error al cargar el activo');
      navigate('/activos');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}/notes`, { withCredentials: true });
      setNotes(response.data.items ?? response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchMediation = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}/mediation`, { withCredentials: true });
      setMediation(response.data);
    } catch (error) {
      console.error('Error fetching mediation:', error);
    }
  };

  const fetchCalls = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}/calls`, { withCredentials: true });
      setCalls(response.data.items ?? response.data);
    } catch (error) {
      console.error('Error fetching calls:', error);
    }
  };

  const fetchWhatsapp = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}/whatsapp`, { withCredentials: true });
      setWhatsapp(response.data.items ?? response.data);
    } catch (error) {
      console.error('Error fetching whatsapp:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/assets/${id}/contacts`, { withCredentials: true });
      setContacts(response.data.items || response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchDocuments = async () => {
    try { const r = await axios.get(`${API}/assets/${id}/documents`, { withCredentials: true }); setDocuments(r.data.items || r.data); } catch {}
  };

  const fetchInteractions = async () => {
    try { const r = await axios.get(`${API}/assets/${id}/interactions`, { withCredentials: true }); setInteractions(r.data.items ?? r.data); } catch {}
  };

  const fetchValoracionesIA = async () => {
    try { const r = await axios.get(`${API}/assets/${id}/valoraciones-ia`, { withCredentials: true }); setValoracionesIA(r.data.items ?? r.data); } catch {}
  };

  const fetchUsers = async () => {
    if (!['superadmin', 'admin', 'gestor'].includes(userRole)) { setUsers([]); return; }
    try { const r = await axios.get(`${API}/users`, { withCredentials: true }); setUsers(r.data.items ?? r.data); } catch {}
  };

  const fetchTasks = async () => {
    try { const r = await axios.get(`${API}/assets/${id}/tasks`, { withCredentials: true }); setTasks(r.data.items ?? r.data); } catch {}
  };

  const fetchTaskAssignableUsers = async () => {
    try { const r = await axios.get(`${API}/assets/${id}/task-assignable-users`, { withCredentials: true }); setTaskAssignableUsers(r.data.items ?? r.data); } catch {}
  };

  const handlePublish = async () => {
    if (!publishMsg.trim()) { toast.error('Escribe un mensaje'); return; }
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/assets/${id}/publish`, { tipo: publishTipo, mensaje: publishMsg }, { withCredentials: true });
      toast.success(`Publicado. ${res.data.notified_inversores} inversores notificados.`);
      setShowPublish(false);
      setPublishMsg('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setPublishing(false); }
  };

  const isCD = asset?.tipo_activo_operativo === 'COMPRA_DIRECTA';

  const runAIValuation = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai-valuation`, aiInput, { withCredentials: true });
      setAiResult(response.data);
      // Save to backend
      await axios.post(`${API}/assets/${id}/save-valuation`, response.data, { withCredentials: true });
      fetchValoracionesIA();
      toast.success('Valoración guardada');
    } catch (error) {
      toast.error('Error en valoración IA');
    } finally {
      setAiLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const handleCatastro = async () => {
    if (!asset?.referencia_catastral) { toast.error('Sin referencia catastral'); return; }
    setCatastroLoading(true);
    try {
      const res = await axios.get(`${API}/catastro/${encodeURIComponent(asset.referencia_catastral)}`, { withCredentials: true });
      const d = res.data.error ? null : res.data.data;
      const c = res.data.coordenadas || {};
      const updates = {};
      if (d) {
        updates.fuente_metros = 'catastro';
        updates.metros_estimados = false;
        if (d.superficie_construida > 0) updates.metros = d.superficie_construida;
        // Prioridad Catastro: la dirección oficial sobrescribe la manual (se guarda la anterior en `direccion_original`).
        if (d.direccion) {
          if (asset.direccion && !asset.direccion_original) updates.direccion_original = asset.direccion;
          updates.direccion = d.direccion;
        }
        if (d.municipio && !asset.municipio) updates.municipio = d.municipio;
        if (d.provincia && !asset.provincia) updates.provincia = d.provincia;
        if (d.uso) updates.uso_catastro = d.uso;
        if (d.anio_construccion) updates.anio_construccion = d.anio_construccion;
      }
      // Coordenadas: sin regla previa → solo se completan si están vacías; nunca se borran.
      const tieneCoords = asset.latitud != null && asset.longitud != null;
      const coordsOk = c.status === 'ok' && c.latitud != null && c.longitud != null;
      if (c.rc_parcela && !asset.rc_parcela) updates.rc_parcela = c.rc_parcela;
      if (!tieneCoords && coordsOk) {
        Object.assign(updates, {
          latitud: c.latitud, longitud: c.longitud, coordenadas_srs: 'EPSG:4326',
          coordenadas_fuente: c.fuente, coordenadas_status: 'ok',
          coordenadas_updated_at: new Date().toISOString(),
        });
      } else if (!tieneCoords) {
        updates.coordenadas_status = 'pendiente';
      }
      if (!d && !coordsOk) {
        if (!tieneCoords) await axios.put(`${API}/assets/${id}`, { ...asset, ...updates }, { withCredentials: true });
        toast.error((res.data.message || 'No se han podido obtener datos') + (tieneCoords ? '' : ' · Ubicación pendiente'));
        fetchAsset();
        return;
      }
      await axios.put(`${API}/assets/${id}`, { ...asset, ...updates }, { withCredentials: true });
      const partes = [];
      if (d) {
        partes.push(`${d.superficie_construida} m² · ${d.uso}`);
        if (d.direccion && d.direccion !== asset.direccion) partes.push('Dirección actualizada');
      } else {
        partes.push(`Datos no disponibles (${res.data.message || 'error'})`);
      }
      if (tieneCoords) partes.push('Coordenadas existentes conservadas');
      else if (coordsOk) partes.push('Coordenadas obtenidas');
      else partes.push(`Ubicación pendiente${c.message ? ` (${c.message})` : ''}`);
      const parcial = !d || (!tieneCoords && !coordsOk);
      (parcial ? toast.warning : toast.success)(`Catastro: ${partes.join(' · ')}`, { duration: parcial ? 7000 : 4000 });
      fetchAsset();
    } catch (err) {
      const status = err.response?.status;
      const backendMsg = err.response?.data?.detail || err.response?.data?.message;
      if (status === 429) {
        toast.error(backendMsg || 'Catastro: límite por hora agotado. Reintenta en ~1h.', { duration: 7000 });
      } else if (backendMsg) {
        toast.error(backendMsg);
      } else {
        toast.error('Error al consultar Catastro');
      }
    } finally { setCatastroLoading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!asset) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="asset-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={() => {
              // Prefer browser history so filters applied in the list page
              // are preserved on navigation back. Fallback to /activos when
              // the detail was opened directly (no history entry).
              if (window.history.length > 1) navigate(-1);
              else navigate('/activos');
            }}
            className="p-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="heading-1 break-words">{asset.direccion || 'Sin dirección'}</h1>
            <div className="flex items-center gap-3 mt-1 text-slate-500 flex-wrap">
              {asset.codigo_npl && (
                <span className="inline-flex items-center gap-1 text-lg font-semibold bg-slate-900 text-white px-2 py-0.5 rounded" data-testid="asset-codigo-npl">
                  {asset.codigo_npl}
                </span>
              )}
              <AssetShareButton assetId={asset.id} asset={asset} />
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{asset.municipio}{asset.provincia && `, ${asset.provincia}`}{asset.zona_barrio && ` · ${asset.zona_barrio}`}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <button onClick={() => navigate(`/activos/${id}/editar`)} data-testid="edit-btn">
              <Edit2 className="w-4 h-4 mr-2" />Editar
            </button>
          )}
          {(isSuperadmin || userRole === 'admin' || userRole === 'gestor') && (
            <button onClick={() => setShowPublish(!showPublish)} className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1.5" data-testid="publish-btn">
              <Send className="w-3.5 h-3.5" /> Publicar
            </button>
          )}
        </div>
      </div>

      {/* Overview Card */}
      <div className="card-base p-6" data-testid="asset-overview">
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="label-text mb-1">Referencia Catastral</p>
            <p className="text-lg font-medium text-slate-900 break-words" data-testid="asset-cadastral-reference">{asset.referencia_catastral || '-'}</p>
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {asset.referencia_catastral ? (
                  <>
                    <button onClick={handleCatastro} disabled={catastroLoading}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-50 transition-colors"
                      data-testid="catastro-btn">
                      {catastroLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ScanLine className="w-3 h-3" />}
                      {catastroLoading ? 'Consultando...' : 'Obtener datos Catastro'}
                    </button>
                    <a
                      href="https://www1.sedecatastro.gob.es/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // Pre-copia la RC al portapapeles para que el usuario solo pegue
                        if (asset?.referencia_catastral && navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(asset.referencia_catastral)
                            .then(() => toast.success(`RC copiada: ${asset.referencia_catastral}`))
                            .catch(() => { /* ignorar */ });
                        }
                      }}
                      className="text-xs font-medium text-slate-700 hover:text-slate-900 flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50 transition-colors"
                      data-testid="catastro-ficha-btn"
                      title="Copia la RC al portapapeles y abre la sede del Catastro (pégala en su buscador)"
                    >
                      <ExternalLink className="w-3 h-3" /> Abrir sede Catastro
                    </a>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      const parts = [asset.direccion, asset.municipio, asset.provincia].filter(Boolean);
                      const text = parts.join(', ');
                      try {
                        if (text && navigator.clipboard) {
                          await navigator.clipboard.writeText(asset.direccion || text);
                          toast.success(`Copiada dirección al portapapeles: ${asset.direccion || text}`);
                        }
                      } catch (_) { /* ignore clipboard errors */ }
                      window.open("https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?buscar=S", "_blank", "noopener,noreferrer");
                    }}
                    className="text-xs font-medium text-amber-700 hover:text-amber-900 flex items-center gap-1 border border-amber-200 rounded-md px-2 py-1 hover:bg-amber-50 transition-colors"
                    data-testid="catastro-buscar-btn"
                    title={`Abre el buscador por dirección en la Sede del Catastro${asset.direccion ? ` · Busca: ${asset.direccion}${asset.municipio ? ', ' + asset.municipio : ''}` : ''}`}
                  >
                    <ExternalLink className="w-3 h-3" /> Buscar Ref. Cat. por dirección
                  </button>
                )}
              </div>
            )}
            {asset.fuente_metros === 'catastro' && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> Datos de Catastro</p>
            )}
          </div>
          <div>
            <p className="label-text mb-1">Tipo Inmueble</p>
            <p className="text-lg font-medium text-slate-900">{asset.tipo_activo || '-'}</p>
          </div>
          <div>
            <p className="label-text mb-1">ROI Venta</p>
            <RoiValue
              value={getRoiVentaValue(asset)}
              isReal={hasRealRoiVenta(asset)}
              kind="venta"
              size="lg"
              testid="roi-venta"
            />
            {/* ROI Alquiler — sólo se muestra si el activo tiene un valor calculado.
                Fuente única: asset.rental_analysis.estimated_rental_roi.
                Diferencia visual Real (manual_monthly_rent) vs Estimado
                (auto-calc Fotocasa). */}
            {getRoiAlquilerValue(asset) != null && (
              <div className="mt-3" data-testid="roi-alquiler-wrapper">
                <p className="label-text mb-1">ROI Alquiler</p>
                <RoiValue
                  value={getRoiAlquilerValue(asset)}
                  isReal={hasRealRoiAlquiler(asset)}
                  kind="alquiler"
                  size="lg"
                  testid="roi-alquiler"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-slate-100">
          {asset.tipo_activo_operativo && (
            <span className="badge-gold font-semibold" data-testid="badge-tipo-operativo">{
              {CREDITO:'CRÉDITO',SUBASTA:'SUBASTA',REMATE:'REMATE',COMPRA_DIRECTA:'COMPRA DIRECTA',OCUPADO:'OCUPADO'}[asset.tipo_activo_operativo] || asset.tipo_activo_operativo
            }</span>
          )}
          {/* Etapa editable — el usuario puede mover el activo libremente
              entre las 6 etapas del pipeline sin orden obligatorio.
              Bloqueada cuando estado=cerrado (spec Feb 2026 §4). */}
          {/* (Feb 2026 mediacion_activa) Si `mediacion_activa=true` y la
              fase actual es oferta/proceso_compra, se muestra la etiqueta
              MEDIACIÓN junto a la fase real. Es puramente visual (misma
              clase que el chip de fase). No editable — se cambia desde el
              toggle de la ficha de edición. */}
          {asset.mediacion_activa && (asset.etapa_visual === 'oferta' || asset.etapa_visual === 'proceso_compra') && (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ETAPA_VISUAL_BADGES['mediacion']}`}
              title="El activo se mantiene en mediación activa (fase paralela)"
              data-testid="header-chip-etapa-mediacion-extra"
            >
              MEDIACIÓN
            </span>
          )}
          <HeaderInlineChip
            assetId={asset.id}
            field="etapa_visual"
            value={asset.etapa_visual || ''}
            options={ETAPA_VISUAL_OPTIONS}
            badgeClass={ETAPA_VISUAL_BADGES[asset.etapa_visual] || 'bg-slate-100 text-slate-600 border-slate-200'}
            placeholder="— sin fase —"
            testid="header-chip-etapa"
            disabled={!canEdit || asset.estado === 'cerrado'}
            disabledTitle={isMediador
              ? 'Solicita el cambio al Gestor o Administrador'
              : (asset.estado === 'cerrado' ? 'Fase bloqueada: el activo está CERRADO' : undefined)}
            onDisabledClick={isMediador ? (() => setMediadorReadOnlyField('fase')) : undefined}
            tooltip={TOOLTIP_ETAPA}
            beforeSave={(newVal) => {
              // Intercepta el paso a "mediacion" → abre el modal obligatorio
              // de asignación de mediador. Bloquea el quick-update por
              // defecto (retorna true) para que el modal ejecute su propio
              // patch con etapa_visual + mediadores.
              if (newVal === 'mediacion') {
                setMediacionModalOpen(true);
                return true;
              }
              // (Feb 2026) Pasar a "En Gestión" exige al menos una tarea
              // pendiente con responsable asignado (pestaña TAREAS). El
              // modal comprueba si el activo ya la tiene (confirma directo)
              // o abre el formulario de tarea obligatoria.
              if (newVal === 'en_gestion') {
                setTareaGestionModalOpen(true);
                return true;
              }
              // (Feb 2026 mediacion_activa) Al pasar a Oferta o Proceso
              // Compra se pregunta si el activo debe seguir contabilizado en
              // la card Mediación del Flujo de Adquisición.
              if (newVal === 'oferta' || newVal === 'proceso_compra') {
                setConfirmMediacionActiva({ value: newVal });
                return true;
              }
              return false;
            }}
            onSaved={(updated) => updated ? setAsset(updated) : fetchAsset()}
          />
          {/* (Feb 2026) Contador de tareas abiertas (pendiente_agendar +
              programada, incluye vencidas). Al pulsar, abre la pestaña
              TAREAS directamente. */}
          {tasks.filter(t => t.estado === 'pendiente_agendar' || t.estado === 'programada').length > 0 && (
            <button
              type="button"
              onClick={() => setActiveTab('tareas')}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-200 transition-colors"
              data-testid="header-chip-tareas-pendientes"
            >
              TAREAS PENDIENTES · {tasks.filter(t => t.estado === 'pendiente_agendar' || t.estado === 'programada').length}
            </button>
          )}
          <HeaderInlineChip
            assetId={asset.id}
            field="estado"
            value={asset.estado || ''}
            options={ESTADO_OPTIONS}
            badgeClass={
              asset.estado === 'activo' ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : asset.estado === 'standby' ? 'bg-slate-200 text-slate-700 border-slate-300'
              : asset.estado === 'cerrado' || asset.estado === 'papelera' ? 'bg-slate-100 text-slate-500 border-slate-200'
              : 'bg-blue-100 text-blue-700 border-blue-200'
            }
            placeholder="— sin estado —"
            testid="header-chip-estado"
            disabled={!canEdit}
            disabledTitle={isMediador ? 'Solicita el cambio al Gestor o Administrador' : undefined}
            onDisabledClick={isMediador ? (() => setMediadorReadOnlyField('estado')) : undefined}
            tooltip={TOOLTIP_ESTADO}
            beforeSave={(newVal) => {
              // (Feb 2026) Pasar a "cerrado" exige elegir motivo de cierre.
              // Interceptamos el quick-update: abrimos el modal y el confirm
              // hace el PATCH con estado + descarte_motivos.
              if (newVal === 'cerrado') {
                setClosureModalOpen(true);
                return true;
              }
              return false;
            }}
            onSaved={(updated) => updated ? setAsset(updated) : fetchAsset()}
          />
          {asset.tipo_activo_operativo === 'CREDITO' && (
            <HeaderInlineChip
              assetId={asset.id}
              field="fase_judicial"
              value={asset.fase_judicial || ''}
              options={FASE_JUDICIAL_LIST}
              badgeClass="bg-purple-50 text-purple-700 border-purple-200"
              placeholder="— sin fase judicial —"
              testid="header-chip-fase-judicial"
              disabled={!canEdit}
              onSaved={(updated) => updated ? setAsset(updated) : fetchAsset()}
            />
          )}
          <HeaderInlineChip
            assetId={asset.id}
            field="prioridad"
            value={asset.prioridad || ''}
            options={PRIORIDAD_OPTIONS}
            badgeClass={
              asset.prioridad === 'urgente' ? 'bg-red-100 text-red-700 border-red-200'
              : asset.prioridad === 'alta' ? 'bg-orange-100 text-orange-700 border-orange-200'
              : asset.prioridad === 'normal' ? 'bg-blue-50 text-blue-700 border-blue-200'
              : asset.prioridad === 'baja' ? 'bg-slate-100 text-slate-600 border-slate-200'
              : asset.prioridad === 'no_prioritario' ? 'bg-slate-50 text-slate-400 border-slate-200'
              : 'bg-slate-100 text-slate-600 border-slate-200'
            }
            placeholder="— sin prioridad —"
            testid="header-chip-prioridad"
            disabled={!canEdit}
            onSaved={(updated) => updated ? setAsset(updated) : fetchAsset()}
          />
          {/* Botón toggle "SOLICITAR REVISIÓN" — visible para admin, gestor
              y (Feb 2026) también para mediador (que puede solicitarla en
              activos donde está asignado). Inversores no lo ven.
              Bloqueado si el estado es incompatible (descartado/cerrado/papelera). */}
          {!isInversor && (() => {
            const reviewIncompatible = ['descartado', 'cerrado', 'papelera'].includes(asset.estado);
            const isMarked = !!asset.marked_for_review;
            const disabled = reviewIncompatible;
            // Al MARCAR abrimos el modal (destinatarios + nota). Al desmarcar
            // también abrimos un modal (nota obligatoria) para notificar al
            // solicitante original con contexto de la resolución.
            const handleClick = () => {
              if (disabled) return;
              if (!isMarked) {
                setShowReviewModal(true);
              } else {
                setShowUnmarkChoice(true);
              }
            };
            if (isMarked) {
              const isCreator = currentUser?.id && asset.marked_for_review_by === currentUser.id;
              return (
                <span
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold border bg-amber-100 text-amber-800 border-amber-300"
                  data-testid="header-badge-review"
                  title="Este activo tiene una revisión solicitada"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                  SOLICITADA REVISIÓN
                  {isCreator && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const r = await axios.get(`${API}/assets/${asset.id}/review-request`, { withCredentials: true });
                          if (r.data?.active) {
                            setEditReviewInitial({
                              note: r.data.note || '',
                              priority: r.data.priority || 'media',
                              recipient_ids: r.data.recipient_ids || [],
                            });
                            setShowEditReviewModal(true);
                          } else {
                            toast.error('No hay solicitud activa que editar');
                          }
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'No se pudo cargar la solicitud');
                        }
                      }}
                      className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 hover:bg-amber-300 text-amber-900 font-medium transition-colors"
                      data-testid="header-btn-edit-review"
                      title="Editar tu solicitud (destinatarios, nota y prioridad)"
                    >
                      Editar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleClick}
                    className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 hover:bg-amber-300 text-amber-900 font-medium transition-colors"
                    data-testid="header-btn-unmark-review"
                    title="Resolver o posponer esta revisión"
                  >
                    Desmarcar
                  </button>
                </span>
              );
            }
            return (
              <button
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-shadow ${disabled
                  ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                  : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 hover:shadow-sm cursor-pointer'}`}
                data-testid="header-btn-request-review"
                title={disabled
                  ? 'No disponible: activo descartado, cerrado o en papelera'
                  : 'Solicita revisión del activo (avisa a admins y gestores)'}
              >
                SOLICITAR REVISIÓN
              </button>
            );
          })()}
          {/* Badges de importación (NUEVO + EN DESCUENTO). Persistentes en BD. */}
          {asset.es_nuevo_import && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold border bg-emerald-100 text-emerald-800 border-emerald-300"
              data-testid="header-badge-nuevo-import"
              title={asset.fecha_primera_importacion
                ? `Nuevo: añadido en la última importación (${new Date(asset.fecha_primera_importacion).toLocaleDateString('es-ES')})`
                : 'Nuevo: añadido en la última importación'}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              NUEVO
              {canEdit && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API}/assets/${asset.id}/dismiss-import-badge`,
                        { tipo: 'nuevo' },
                        { withCredentials: true },
                      );
                      fetchAsset();
                    } catch {
                      toast.error('No se pudo descartar el badge');
                    }
                  }}
                  className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-200 hover:bg-emerald-300 text-emerald-900 font-medium transition-colors"
                  data-testid="header-btn-dismiss-nuevo"
                  title="Descartar este badge"
                >
                  ✕
                </button>
              )}
            </span>
          )}
          {typeof asset.descuento_pct === 'number' && asset.descuento_pct < 0 && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold border bg-orange-100 text-orange-800 border-orange-300"
              data-testid="header-badge-descuento"
              title={
                asset.precio_anterior_importacion && asset.precio_actual_importacion
                  ? `Bajada de precio (${asset.precio_campo_importacion || '—'}): ${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(asset.precio_anterior_importacion)} → ${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(asset.precio_actual_importacion)}`
                  : 'Bajada de precio detectada en la última importación'
              }
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" />
              EN DESCUENTO {`${asset.descuento_pct}%`}
              {canEdit && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(
                        `${API}/assets/${asset.id}/dismiss-import-badge`,
                        { tipo: 'descuento' },
                        { withCredentials: true },
                      );
                      fetchAsset();
                    } catch {
                      toast.error('No se pudo descartar el badge');
                    }
                  }}
                  className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-200 hover:bg-orange-300 text-orange-900 font-medium transition-colors"
                  data-testid="header-btn-dismiss-descuento"
                  title="Descartar este badge"
                >
                  ✕
                </button>
              )}
            </span>
          )}
          {asset.fecha_compra && !asset.fecha_venta && (() => {
            const dias = Math.max(1, Math.round((new Date() - new Date(asset.fecha_compra)) / (1000 * 60 * 60 * 24)));
            return <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium" data-testid="dias-en-curso">{dias} días en curso</span>;
          })()}
        </div>

        <AssetAssignments key={asset.id} asset={asset} canEdit={canManageAssignments(currentUser)} currentUser={currentUser}
          onRefresh={fetchAsset} onAccessLost={() => navigate('/activos')} />
      </div>

      {/* Publish panel */}
      {showPublish && (
        <div className="card-base p-5 space-y-4 animate-fade-in" data-testid="publish-panel">
          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Send className="w-4 h-4 text-blue-600" /> Publicar actualización para inversores</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Tipo de actualización</label>
              <select value={publishTipo} onChange={e => setPublishTipo(e.target.value)} className="input-base" data-testid="publish-tipo">
                <option value="balance">Balance económico actualizado</option>
                <option value="fase">Cambio de fase / estado</option>
                <option value="documentacion">Nueva documentación publicada</option>
                <option value="actualizacion">Actualización general</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500">Destinatarios</label>
              <p className="text-sm text-slate-600 pt-1">{(asset.inversores || []).length} inversores + {(asset.gestores || []).length} gestores</p>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-500">Mensaje para inversores</label>
            <textarea className="input-base min-h-[80px]" value={publishMsg} onChange={e => setPublishMsg(e.target.value)} placeholder="Escribe el mensaje que verán los inversores..." data-testid="publish-msg" />
          </div>
          <div className="flex gap-2">
            <button onClick={handlePublish} disabled={publishing} className="btn-primary flex items-center gap-2" data-testid="publish-confirm-btn">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Publicar
            </button>
            <button onClick={() => setShowPublish(false)} className="btn-secondary">Cancelar</button>
          </div>
        </div>
      )}

      {/* Tabs - pure HTML buttons + conditional divs, no Radix */}
      <div className="space-y-4">
        {isInversor && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2" data-testid="investor-banner">
            <Eye className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Vista inversor — Información publicada (solo lectura)</span>
          </div>
        )}
        <div className="card-base">
          <div className="flex flex-wrap gap-x-1 border-b border-slate-200 p-0" data-testid="tabs-list">
            {(() => {
              // El rol global y el alcance autorizado determinan las pestañas.
              let baseTabs;
              if (isInversor) {
                baseTabs = [['roi','Análisis ROI'],['tareas','Tareas'],['documentos','Documentos'],['balance','Balance económico']];
              } else if (isMediador) {
                // Mediador: pestañas operativas pero sin financiero ni mediación detallada.
                baseTabs = [['datos','Datos'],['notas','Notas'],['tareas','Tareas'],['interacciones','Interacciones'],['contactos','Contactos'],['documentos','Documentos']];
              } else if (canViewResumen) {
                baseTabs = [['resumen','Resumen'],['datos','Datos'],['roi','Análisis ROI'],['valoracion','VENTA'],['notas','Notas'],['tareas','Tareas'],['interacciones','Interacciones'],['contactos','Contactos'],['documentos','Documentos'],['balance','Balance económico']];
              } else {
                // Sin permiso de lectura del proveedor no se habilita Resumen.
                baseTabs = [['datos','Datos'],['roi','Análisis ROI'],['valoracion','VENTA'],['notas','Notas'],['tareas','Tareas'],['interacciones','Interacciones'],['contactos','Contactos'],['documentos','Documentos'],['balance','Balance económico']];
              }
              // Superadmin sees an Actividad tab with the per-asset audit log
              if (isSuperadmin) {
                baseTabs.push(['actividad','Actividad']);
              }
              // Insert the Scoring Crédito tab only for CESIÓN DE CRÉDITO assets
              // (oculto para inversor y mediador — contiene scoring financiero interno)
              if (asset.tipo_activo_operativo === 'CREDITO' && !isInversor && !isMediador) {
                const idx = baseTabs.findIndex(([k]) => k === 'roi');
                if (idx >= 0) baseTabs.splice(idx + 1, 0, ['credito-scoring', 'Scoring Crédito']);
              }
              // Pestaña ALQUILER: inmediatamente a la derecha de "VENTA" (antes "Valoración Salida").
              // Disponible para superadmin, admin y gestor sobre cualquier tipo de
              // activo inmobiliario (todos excepto CESIÓN DE CRÉDITO puro sin inmueble).
              // No se muestra al inversor (no ve valoración) ni al mediador.
              if (!isInversor && !isMediador) {
                const vIdx = baseTabs.findIndex(([k]) => k === 'valoracion');
                if (vIdx >= 0) baseTabs.splice(vIdx + 1, 0, ['alquiler', 'ALQUILER']);
              }
              // Mediación para los 5 tipos con fase de mediación/negociación.
              // El mediador SÍ debe verla (su rol principal); el inversor NO.
              if (!isInversor && ['CREDITO', 'REMATE', 'SUBASTA', 'OCUPADO', 'REO'].includes(asset.tipo_activo_operativo)) {
                const idx2 = baseTabs.findIndex(([k]) => k === 'tareas');
                if (idx2 >= 0) baseTabs.splice(idx2 + 1, 0, ['mediacion','Mediación']);
              }
              return baseTabs;
            })().map(([k,l]) => (
              <button key={k} type="button" onClick={() => setActiveTab(k)} data-testid={`tab-${k}`}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeTab === k ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
              >{l}</button>
            ))}
          </div>
        </div>

        <AssetExclusionPanel asset={asset} user={currentUser} />
        {activeTab === 'resumen' && canViewResumen && (
          <div className="space-y-4" data-testid="asset-summary">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-base p-6 min-w-0" data-testid="summary-main-info">
              <h3 className="heading-3 mb-4">Información Principal</h3>
              <div className="space-y-4" data-testid="summary-main-fields">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2 py-2 border-b border-slate-100 min-w-0" data-testid="summary-proveedor">
                  <span className="text-slate-500 shrink-0">Proveedor</span>
                  <span className="font-medium min-w-0 sm:text-right [overflow-wrap:anywhere]" data-testid="summary-proveedor-value">{asset.proveedor || '-'}</span>
                </div>
                <AssetServicer asset={asset} canEdit={canEditAdminOnlyFields} onSaved={fetchAsset} />
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">ID Proveedor</span>
                  <span className="font-medium">{asset.id_proveedor || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">ID Préstamo</span>
                  <span className="font-medium">{asset.id_prestamo || '-'}</span>
                </div>
                <InlineTextField assetId={asset.id} field="id_finca" value={asset.id_finca} label="ID Finca"
                  editable={canEditAdminOnlyFields} onSaved={fetchAsset} testid="summary-id-finca" />
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Metros</span>
                  <span className="font-medium">{asset.metros ? `${asset.metros} m²` : '-'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Ocupación</span>
                  <span className="font-medium">{asset.ocupacion || '-'}</span>
                </div>
              </div>
            </div>

            <div className="card-base p-6">
              <h3 className="heading-3 mb-4">Datos Económicos</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Precio Compra{asset.precio_compra_es_estimado ? ' *' : ''}</span>
                  <span className="font-medium text-blue-600">{formatCurrency(asset.precio_compra)}{asset.precio_compra_es_estimado ? ' *' : ''}</span>
                </div>
                {isCD ? null : <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Deuda Reclamada{asset.deudas && asset.deudas.length > 1 ? ` (suma ${asset.deudas.length} deudas)` : ''}</span><span className="font-medium">{formatCurrency(asset.deuda_reclamada)}</span></div>}
                {isCD ? null : <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-slate-500">Tasación</span><span className="font-medium">{formatCurrency(asset.tasacion ?? asset.valor_subasta)}</span></div>}
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Valor Referencia</span>
                  <span className="font-medium">{formatCurrency(asset.valor_referencia)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-500">Cargas</span>
                  <span className="font-medium">{formatCurrency(asset.cargas)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Multi-deuda: lista detallada cuando hay varias deudas */}
          {Array.isArray(asset.deudas) && asset.deudas.length > 1 && (
            <DeudasPanel
              deudas={asset.deudas}
              formatCurrency={formatCurrency}
              assetId={id}
              currentUser={currentUser}
              onDeleted={fetchAsset}
            />
          )}

          {/* Participantes en la rentabilidad */}
          {Array.isArray(asset.participantes) && asset.participantes.some(p => (Number(p.aportacion) || 0) > 0) && (
            <ParticipantesPanel participantes={asset.participantes} formatCurrency={formatCurrency} />
          )}

          <div className="card-base p-6">
            <h3 className="heading-3 mb-4">Observaciones</h3>
            <p className="body-text">{asset.observaciones_importacion || 'Sin observaciones'}</p>
          </div>

          <JudicialDataCard asset={asset} canEdit={canEdit} onSaved={fetchAsset} />

          {/* Traceability */}
          <div className="card-base p-6" data-testid="traceability-section">
            <h3 className="heading-3 mb-4">Trazabilidad</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Creado por</p>
                <p className="font-medium text-slate-900 mt-1">{asset.created_by_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fecha creación</p>
                <p className="font-medium text-slate-900 mt-1">{formatDate(asset.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Última modificación por</p>
                <p className="font-medium text-slate-900 mt-1">{asset.updated_by_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Fecha modificación</p>
                <p className="font-medium text-slate-900 mt-1">{formatDate(asset.updated_at)}</p>
              </div>
            </div>
          </div>
          </div>
        )}

        {activeTab === 'datos' && !isInversor && (
          <div className="card-base p-6">
            <h3 className="heading-3 mb-6">Datos del Activo</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ReadItem label="Código NPL" value={asset.codigo_npl} />
              <ReadItem label="Dirección" value={asset.direccion} />
              <ReadItem label="Municipio" value={asset.municipio} />
              <ReadItem label="Provincia" value={asset.provincia} />
              <ReadItem label="Zona / Barrio" value={asset.zona_barrio} />
              <ReadItem label="Código Postal" value={asset.codigo_postal} />
              <CoordItem label="Latitud" value={asset.latitud} asset={asset} testid="asset-latitud" />
              <CoordItem label="Longitud" value={asset.longitud} asset={asset} testid="asset-longitud" />
              <ReadItem label="Punto de acceso para visitas" value={asset.acceso_latitud != null && asset.acceso_longitud != null ? `${Number(asset.acceso_latitud).toFixed(6)}, ${Number(asset.acceso_longitud).toFixed(6)}${asset.acceso_descripcion ? ` · ${asset.acceso_descripcion}` : ''}` : null} />
              <ReadItem label="Referencia Catastral" value={asset.referencia_catastral} />
              <ReadItem label="Tipo Inmueble" value={asset.tipo_activo} />
              <ReadItem label="Metros (m²)" value={asset.metros} />
              <ReadItem label="Ocupación" value={asset.ocupacion} />
              <ReadItem label="Estado" value={asset.estado} />
              <ReadItem label="Fase" value={asset.etapa_visual ? (ETAPA_VISUAL_LABELS[asset.etapa_visual] || asset.etapa_visual) : '-'} />
              {asset.tipo_activo_operativo === 'CREDITO' && (
                <ReadItem label="Fase Judicial" value={asset.fase_judicial ? (FASE_JUDICIAL_LABELS[asset.fase_judicial] || asset.fase_judicial) : '-'} />
              )}
              <ReadItem label="Prioridad" value={asset.prioridad} />
              <ReadItem label="Tipo Activo Operativo" value={TIPO_OP_LABELS[asset.tipo_activo_operativo] || asset.tipo_activo_operativo} />
            </div>

            <h4 className="heading-3 mt-8 mb-4">Datos Económicos y ROI</h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ReadItem label={`Precio Compra${asset.precio_compra_es_estimado ? ' *' : ''}`} value={`${formatCurrency(asset.precio_compra)}${asset.precio_compra_es_estimado ? ' *' : ''}`} />
              {!isCD && <ReadItem label="Tasación" value={formatCurrency(asset.tasacion)} />}
              {!isCD && <ReadItem label="Deuda Reclamada" value={formatCurrency(asset.deuda_reclamada)} />}
              {!isCD && <ReadItem label="Deuda Total" value={formatCurrency(asset.deuda_total)} />}
              {!isCD && <ReadItem label="Valor Adjudicación" value={formatCurrency(asset.valor_adjudicacion)} />}
              <ReadItem label="Valor Referencia" value={formatCurrency(asset.valor_referencia)} />
              <ReadItem
                label={(asset.valor_salida_individual && asset.valor_salida_individual > 0) ? "Valor de Salida (individualizado)" : "Valor de Salida automático"}
                value={formatCurrency(
                  asset.valor_salida_individual
                  || asset.valor_salida_base
                )}
              />
              <ReadItem label="Tiempo Resolución" value={asset.tiempo_resolucion_meses ? `${asset.tiempo_resolucion_meses} meses` : '-'} />
              {!isCD && <ReadItem label="Tasación" value={formatCurrency(asset.tasacion ?? asset.valor_subasta)} />}
              <ReadItem label="Cargas" value={formatCurrency(asset.cargas)} />
              <ReadItem label="Vivienda Habitual" value={asset.vivienda_habitual === 'NO' ? 'No' : asset.vivienda_habitual === 'SI' ? 'Sí' : 'No consta'} />
            </div>
          </div>
        )}

        {activeTab === 'roi' && (
          <ROIAdvancedTab asset={asset} assetId={id} onAssetUpdated={fetchAsset} readOnly={!canEdit} />
        )}

        {activeTab === 'credito-scoring' && asset.tipo_activo_operativo === 'CREDITO' && (
          <CreditoScoringTab asset={asset} assetId={id} readOnly={isInversor} />
        )}

        {activeTab === 'balance' && (
          <BalanceTab asset={asset} assetId={id} readOnly={isInversor} canPublish={canEdit && !isInversor} onAssetUpdated={fetchAsset} />
        )}

        {activeTab === 'valoracion' && (
          <ValoracionSalidaTab asset={asset} assetId={id} onAssetUpdated={fetchAsset} readOnly={isInversor} />
        )}

        {activeTab === 'alquiler' && !isInversor && !isMediador && (
          <RentalAnalysisSection asset={asset} onAssetUpdate={setAsset} />
        )}

        {activeTab === 'notas' && <NotesSection assetId={id} notes={notes} onRefresh={fetchNotes} />}
        {activeTab === 'tareas' && <TasksSection assetId={id} tasks={tasks} assignableUsers={taskAssignableUsers} currentUser={currentUser} assetEtapaVisual={asset.etapa_visual} readOnly={isInversor} onRefresh={() => { fetchTasks(); fetchNotes(); }} onAssetRefresh={fetchAsset} />}
        {activeTab === 'mediacion' && <MediationSection assetId={id} mediation={mediation} onRefresh={fetchMediation} users={users} contacts={contacts} interactions={interactions} onRefreshInteractions={() => { fetchInteractions(); fetchNotes(); }} onRefreshContacts={fetchContacts} />}
        {activeTab === 'interacciones' && <InteractionsSection assetId={id} interactions={interactions} contacts={contacts} users={users} onRefresh={() => { fetchInteractions(); fetchNotes(); }} onRefreshContacts={fetchContacts} />}
        {activeTab === 'contactos' && !isInversor && <ContactsSection assetId={id} contacts={contacts} onRefresh={fetchContacts} users={users} />}
        {activeTab === 'documentos' && <DocumentsSection assetId={id} documents={documents} onRefresh={fetchDocuments} readOnly={isInversor} canEdit={canEdit} canDelete={!isInversor && !isMediador} canDownload={!isMediador} canPreview={true} />}

        {activeTab === 'actividad' && isSuperadmin && <AssetActivitySection assetId={id} />}

        {/* Footer legend — shown on every tab when Precio Compra is estimated */}
        {asset?.precio_compra_es_estimado && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="precio-compra-estimado-legend">
            <span className="font-semibold">*</span>{' '}
            Precio de compra estimado automáticamente a partir del % de descuento sobre deuda. Pendiente de verificar por el usuario.
            {asset.descuento_deuda_pct != null && asset.deuda_total ? (
              <span className="ml-1 text-amber-700">
                (Deuda {new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR'}).format(asset.deuda_total)} × (1 − {asset.descuento_deuda_pct}%))
              </span>
            ) : null}
          </div>
        )}
      </div>
      {/* Modal Solicitar revisión (destinatarios + nota obligatoria). */}
      <RequestReviewModal
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        asset={asset}
        onSuccess={(updated) => { if (updated) setAsset(updated); fetchNotes(); }}
      />
      {/* Modal EDITAR solicitud activa (solo creador). */}
      <RequestReviewModal
        open={showEditReviewModal}
        onClose={() => { setShowEditReviewModal(false); setEditReviewInitial(null); }}
        asset={asset}
        mode="edit"
        initial={editReviewInitial}
        onSuccess={() => { fetchAsset(); fetchNotes(); }}
      />
      {/* Modal de asignación obligatoria de mediador al pasar a fase Mediación. */}
      <MediacionAssignModal
        open={mediacionModalOpen}
        asset={asset}
        onClose={() => setMediacionModalOpen(false)}
        onConfirmed={(updated) => { if (updated) setAsset(updated); else fetchAsset(); }}
      />
      {/* Modal de tarea obligatoria al pasar a fase En Gestión. */}
      <TareaGestionModal
        open={tareaGestionModalOpen}
        asset={asset}
        onClose={() => setTareaGestionModalOpen(false)}
        onConfirmed={() => { setTareaGestionModalOpen(false); fetchAsset(); fetchTasks(); }}
      />
      {/* Popup ¿Mantener en mediación simultáneamente? al pasar a Oferta o
          Proceso compra (spec Feb 2026 mediacion_activa). */}
      <ConfirmMediacionActivaModal
        open={!!confirmMediacionActiva}
        targetFaseLabel={confirmMediacionActiva?.value === 'oferta' ? 'Oferta' : 'Proceso compra'}
        onClose={() => setConfirmMediacionActiva(null)}
        onAnswer={async (activa) => {
          const target = confirmMediacionActiva?.value;
          setConfirmMediacionActiva(null);
          try {
            const r = await axios.patch(
              `${API}/assets/${asset.id}/quick-update`,
              { etapa_visual: target, mediacion_activa: activa },
              { withCredentials: true },
            );
            toast.success('Fase actualizada' + (activa ? ' · mediación activa' : ''));
            if (r.data?.asset) setAsset(r.data.asset); else fetchAsset();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
          }
        }}
      />
      {/* Modal OBLIGATORIO "Motivo de cierre" al pasar estado a cerrado
          desde el chip del header (spec Feb 2026). */}
      <ClosureReasonModal
        open={closureModalOpen}
        onClose={() => setClosureModalOpen(false)}
        onConfirm={async (motivos) => {
          setClosureModalOpen(false);
          try {
            const r = await axios.patch(
              `${API}/assets/${asset.id}/quick-update`,
              { estado: 'cerrado', descarte_motivos: motivos },
              { withCredentials: true },
            );
            toast.success('Activo cerrado');
            if (r.data?.asset) setAsset(r.data.asset); else fetchAsset();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
          }
        }}
      />
      {/* Modal informativo Mediador: campo Estado/Fase no editable + CTA
          para lanzar Solicitud de Revisión al Gestor/Admin. */}
      {mediadorReadOnlyField && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="mediador-readonly-modal">
          <div className="rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}>
            <div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Campo no editable
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Como <b>Mediador</b>, no puedes modificar directamente el campo <b>{mediadorReadOnlyField === 'estado' ? 'ESTADO' : 'FASE'}</b>.
                Solicita el cambio al <b>Gestor</b> o <b>Administrador</b> asignado.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setMediadorReadOnlyField(null)} className="btn-secondary text-sm" data-testid="mediador-readonly-cancel">
                Cerrar
              </button>
              <button
                onClick={() => { setMediadorReadOnlyField(null); setShowReviewModal(true); }}
                className="btn-primary text-sm"
                data-testid="mediador-readonly-request-review"
              >
                Solicitar revisión
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Resolver revisión (nota obligatoria; notifica al solicitante). */}
      <ResolveReviewModal
        open={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        asset={asset}
        onSuccess={(updated) => { if (updated) setAsset(updated); fetchNotes(); }}
      />
      {/* Panel de elección al pulsar "Desmarcar": Resolver o Posponer. */}
      <ResolveOrPostponeChoiceModal
        open={showUnmarkChoice}
        onClose={() => setShowUnmarkChoice(false)}
        onResolve={() => { setShowUnmarkChoice(false); setShowResolveModal(true); }}
        onPostpone={handleChoosePostponeFromAsset}
      />
      {/* Diálogo de posponer (fecha + nota), reutilizado de Pendientes de revisión. */}
      {postponeReviewItem && (
        <ReviewPostponeDialog
          item={postponeReviewItem}
          endpoint={`/assets/${asset.id}/review-request/posponer`}
          onCancel={() => setPostponeReviewItem(null)}
          onSaved={async () => {
            setPostponeReviewItem(null);
            toast.success('Revisión pospuesta para todos los destinatarios');
            fetchNotes();
          }}
        />
      )}
    </div>
  );
};

// Helper Components
// Coordenadas WGS84. Las de Catastro son el centroide de la parcela, no la entrada exacta.
const CoordItem = ({ label, value, asset, testid }) => {
  const known = value !== null && value !== undefined;
  const fromCatastro = asset.coordenadas_fuente === 'catastro_centroide_parcela';
  return (
    <div data-testid={testid}>
      <p className="text-xs text-slate-500 tracking-wide">{label} <span className="text-slate-400">(WGS84)</span></p>
      <p className="font-medium text-slate-900 mt-1 tabular-nums">{known ? Number(value).toFixed(6) : '-'}</p>
      {known && fromCatastro && <p className="text-[11px] text-slate-400 mt-0.5">Centroide de parcela (Catastro)</p>}
      {!known && asset.coordenadas_status === 'pendiente' && (
        <p className="text-[11px] text-amber-600 mt-0.5" data-testid={`${testid}-pendiente`}>Ubicación pendiente</p>
      )}
    </div>
  );
};

const ReadItem = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 tracking-wide">{label}</p>
    <p className="font-medium text-slate-900 mt-1">{value || '-'}</p>
  </div>
);

const TIPO_OP_LABELS = {
  CREDITO: 'Crédito',
  SUBASTA: 'Subasta',
  REMATE: 'Remate',
  COMPRA_DIRECTA: 'Compra Directa',
  OCUPADO: 'Ocupado'
};

// Notes Section
const NotesSection = ({ assetId, notes, onRefresh }) => {
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/assets/${assetId}/notes`, { asset_id: assetId, contenido: newNote }, { withCredentials: true });
      setNewNote('');
      onRefresh();
      toast.success('Nota añadida');
    } catch (error) {
      toast.error('Error al añadir nota');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditingContent(note.contenido);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const saveEdit = async (noteId) => {
    if (!editingContent.trim()) return;
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/assets/${assetId}/notes/${noteId}`,
        { asset_id: assetId, contenido: editingContent },
        { withCredentials: true }
      );
      toast.success('Nota actualizada');
      cancelEdit();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al editar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    try {
      await axios.delete(`${API}/assets/${assetId}/notes/${noteId}`, { withCredentials: true });
      toast.success('Nota eliminada');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="card-base p-6">
      <h3 className="heading-3 mb-6 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Notas Internas</h3>
      <div className="mb-6">
        <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Escribe una nueva nota..." className="input-base min-h-[100px] mb-3" data-testid="new-note-input" />
        <button onClick={handleAddNote} disabled={saving || !newNote.trim()} className="btn-primary" data-testid="add-note-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />Añadir nota</>}
        </button>
      </div>
      <div className="space-y-4">
        {notes.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No hay notas todavía</p>
        ) : (
          notes.map((note) => {
            const isEditing = editingId === note.id;
            const isInteraction = note.kind === 'interaction';
            const isRevision = note.kind === 'revision';
            const isTarea = note.kind === 'tarea';
            const revisionOpen = isRevision && note.review_open;
            const tareaOpen = isTarea && note.task_open;
            const containerClass = isRevision
              ? (revisionOpen ? 'bg-amber-50/50 border-amber-200' : 'bg-emerald-50/40 border-emerald-200')
              : isTarea
              ? (tareaOpen ? 'bg-fuchsia-50/50 border-fuchsia-200' : 'bg-emerald-50/40 border-emerald-200')
              : (isInteraction ? 'bg-blue-50/40 border-blue-100' : 'bg-slate-50 border-slate-100');
            return (
              <div
                key={note.id}
                className={`p-4 rounded-lg border ${containerClass}`}
                data-testid={`note-${note.id}`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="input-base min-h-[80px]"
                      data-testid={`note-edit-input-${note.id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        className="text-xs px-3 py-1.5 rounded text-slate-600 hover:bg-slate-200"
                        data-testid={`note-cancel-${note.id}`}
                      >Cancelar</button>
                      <button
                        onClick={() => saveEdit(note.id)}
                        disabled={savingEdit || !editingContent.trim()}
                        className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                        data-testid={`note-save-${note.id}`}
                      >{savingEdit ? 'Guardando...' : 'Guardar'}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {isInteraction && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded mb-2"
                        title="Registrada en la pestaña Interacciones"
                      >
                        Interacción
                      </span>
                    )}
                    {isRevision && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded mb-2 ${revisionOpen ? 'text-amber-800 bg-amber-100 border border-amber-300' : 'text-emerald-800 bg-emerald-100 border border-emerald-300'}`}
                        title={revisionOpen ? 'Revisión pendiente de resolver' : 'Revisión resuelta'}
                        data-testid={`note-revision-badge-${note.id}`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${revisionOpen ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                        {revisionOpen ? 'REVISIÓN · pendiente' : 'REVISIÓN · resuelta'}
                      </span>
                    )}
                    {isTarea && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded mb-2 ${tareaOpen ? 'text-fuchsia-800 bg-fuchsia-100 border border-fuchsia-300' : 'text-emerald-800 bg-emerald-100 border border-emerald-300'}`}
                        title={tareaOpen ? 'Tarea pendiente' : 'Tarea cerrada'}
                        data-testid={`note-tarea-badge-${note.id}`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${tareaOpen ? 'bg-fuchsia-500' : 'bg-emerald-500'}`} />
                        {tareaOpen ? 'TAREA · pendiente' : 'TAREA · cerrada'}
                      </span>
                    )}
                    <p className="text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`note-content-${note.id}`}>{note.contenido}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-500">
                      <span data-testid={`note-author-${note.id}`}>{note.autor}</span>
                      <span>•</span>
                      <span data-testid={`note-date-${note.id}`}>{new Date(note.created_at).toLocaleString('es-ES')}</span>
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <>
                          <span>•</span>
                          <span className="italic">editada {new Date(note.updated_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        </>
                      )}
                      {note.can_edit && (
                        <button
                          onClick={() => startEdit(note)}
                          className="ml-auto text-blue-600 hover:underline flex items-center gap-1"
                          data-testid={`note-edit-${note.id}`}
                          title="Editar tu nota"
                        >
                          <Pencil className="w-3 h-3" /> Editar
                        </button>
                      )}
                      {note.can_delete && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className={`${note.can_edit ? '' : 'ml-auto'} text-red-600 hover:underline flex items-center gap-1`}
                          data-testid={`note-delete-${note.id}`}
                          title="Eliminar nota"
                        >
                          <Trash2 className="w-3 h-3" /> Eliminar
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Mediation Section — 3 bloques: Contacto · Negociación · Notas
// Solo se renderiza cuando el tipo de activo pertenece a {CREDITO, SUBASTA, OCUPADO, REO}
// (el gating lo decide la tab de AssetDetailPage; aquí asumimos render = tipo válido).
const ESTADO_MEDIACION_OPCIONES = [
  { v: 'PENDIENTE',       l: 'Pendiente' },
  { v: 'SIN_CONTACTAR',   l: 'Sin contactar' },
  { v: 'CONTACTADO',      l: 'Contactado' },
  { v: 'EN_NEGOCIACION',  l: 'En negociación' },
  { v: 'PACTO_POSIBLE',   l: 'Pacto posible' },
  { v: 'NO_INTERESADO',   l: 'No interesado' },
  { v: 'ILLOCALIZABLE',   l: 'Illocalizable' },
  { v: 'CERRADO',         l: 'Cerrado' },
];

const emptyMediation = {
  estado_mediacion: '',
  // Contacto
  contactado: '',
  numero_contactos: '',
  fecha_primer_contacto: '',
  fecha_ultimo_contacto: '',
  // Negociación
  receptividad: '',
  voluntad_pacto: '',
  capacidad_economica: '',
  probabilidad_acuerdo: '',
  pide_compensacion: '',
  importe_compensacion: '',
  plazo_pacto: '',
  // Notas
  notas_mediador: '',
  // Ocupación y posesión
  ocupacion_confirmada: '',
  tipo_ocupante_actual: '',
  posesion_probable_tras_adjudicacion: '',
  riesgo_lanzamiento: '',
  riesgo_resistencia_salida: '',
};

const TIPO_OCUPANTE_OPCIONES = [
  { v: 'DEUDOR',              l: 'Deudor' },
  { v: 'INQUILINO',           l: 'Inquilino' },
  { v: 'FAMILIAR',            l: 'Familiar' },
  { v: 'OKUPA',               l: 'Okupa' },
  { v: 'EXPROPIETARIO',       l: 'Ex-propietario' },
  { v: 'TERCERO_SIN_TITULO',  l: 'Tercero sin título' },
  { v: 'VACIO',               l: 'Vacío' },
  { v: 'DESCONOCIDO',         l: 'Desconocido' },
];

const MediationSection = ({ assetId, mediation, onRefresh, users, contacts, interactions, onRefreshInteractions, onRefreshContacts }) => {
  // Interactions registered from the Mediación tab (origen='mediacion')
  const mediacionInteractions = (interactions || []).filter(i => i.origen === 'mediacion');

  // Form A: estado + negociación + notas (persists to /mediation)
  const [form, setForm] = useState(emptyMediation);
  const [saving, setSaving] = useState(false);

  // Form B: new interaction to register from this tab
  const nowDate = () => new Date().toISOString().slice(0, 10);
  const nowTime = () => new Date().toTimeString().slice(0, 5);
  const emptyInteraction = {
    tipo: 'llamada',
    contacto_id: '',
    contacto_nombre: '',
    fecha: '',
    hora: '',
    resumen: '',
  };
  const [inter, setInter] = useState(emptyInteraction);
  const [showNewContact, setShowNewContact] = useState(false);
  const [savingInter, setSavingInter] = useState(false);

  useEffect(() => {
    if (!mediation) { setForm(emptyMediation); return; }
    setForm({
      estado_mediacion: mediation.estado_mediacion || '',
      contactado: mediation.contactado || '',
      numero_contactos: mediation.numero_contactos ?? 0,
      fecha_primer_contacto: mediation.fecha_primer_contacto || '',
      fecha_ultimo_contacto: mediation.fecha_ultimo_contacto || '',
      receptividad: mediation.receptividad ?? '',
      voluntad_pacto: mediation.voluntad_pacto ?? '',
      capacidad_economica: mediation.capacidad_economica ?? '',
      probabilidad_acuerdo: mediation.probabilidad_acuerdo ?? '',
      pide_compensacion: mediation.pide_compensacion || '',
      importe_compensacion: mediation.importe_compensacion ?? '',
      plazo_pacto: mediation.plazo_pacto ?? '',
      notas_mediador: mediation.notas_mediador || mediation.resumen_negociacion || '',
      // Ocupación y posesión
      ocupacion_confirmada: mediation.ocupacion_confirmada || '',
      tipo_ocupante_actual: mediation.tipo_ocupante_actual || '',
      posesion_probable_tras_adjudicacion: mediation.posesion_probable_tras_adjudicacion || '',
      riesgo_lanzamiento: mediation.riesgo_lanzamiento ?? '',
      riesgo_resistencia_salida: mediation.riesgo_resistencia_salida ?? '',
    });
  }, [mediation]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateInter = (patch) => setInter((i) => ({ ...i, ...patch }));

  // When the mediator marks "Sí" in ¿Contactado?, prefill the new interaction
  // fecha/hora with the current date/time (still editable by the user).
  useEffect(() => {
    if (form.contactado === 'SI' && !inter.fecha) {
      setInter((i) => ({ ...i, fecha: nowDate(), hora: nowTime() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contactado]);

  const toInt = (v, min, max) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return null;
    return Math.min(max, Math.max(min, n));
  };
  const toFloat = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
  };

  const handleSaveMediation = async () => {
    setSaving(true);
    try {
      const payload = {
        asset_id: assetId,
        estado_mediacion: form.estado_mediacion || null,
        contactado: form.contactado || null,
        fecha_primer_contacto: form.fecha_primer_contacto || null,
        receptividad: toInt(form.receptividad, 1, 5),
        voluntad_pacto: toInt(form.voluntad_pacto, 1, 5),
        capacidad_economica: toInt(form.capacidad_economica, 1, 5),
        probabilidad_acuerdo: toInt(form.probabilidad_acuerdo, 0, 100),
        pide_compensacion: form.pide_compensacion || null,
        importe_compensacion: toFloat(form.importe_compensacion),
        plazo_pacto: toInt(form.plazo_pacto, 0, 240),
        notas_mediador: form.notas_mediador || null,
        // Ocupación y posesión
        ocupacion_confirmada: form.ocupacion_confirmada || null,
        tipo_ocupante_actual: form.tipo_ocupante_actual || null,
        posesion_probable_tras_adjudicacion: form.posesion_probable_tras_adjudicacion || null,
        riesgo_lanzamiento: toInt(form.riesgo_lanzamiento, 1, 5),
        riesgo_resistencia_salida: toInt(form.riesgo_resistencia_salida, 1, 5),
      };
      await axios.post(`${API}/assets/${assetId}/mediation`, payload, { withCredentials: true });
      onRefresh();
      toast.success('Mediación guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterInteraction = async () => {
    if (!inter.fecha) { toast.error('Fecha obligatoria'); return; }
    if (!inter.contacto_id && !inter.contacto_nombre) {
      toast.error('Selecciona un contacto'); return;
    }
    setSavingInter(true);
    try {
      const payload = { ...inter, asset_id: assetId, origen: 'mediacion' };
      if (inter.contacto_id && inter.contacto_id !== 'new') {
        const c = (contacts || []).find((x) => x.id === inter.contacto_id);
        payload.contacto_nombre = c?.nombre || '';
      }
      await axios.post(`${API}/assets/${assetId}/interactions`, payload, { withCredentials: true });
      // Reset interaction form; keep the rest of the mediación form intact
      setInter(emptyInteraction);
      setShowNewContact(false);
      await Promise.all([onRefreshInteractions(), onRefresh()]);
      if (inter.contacto_id === 'new') onRefreshContacts();
      toast.success('Interacción registrada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar');
    } finally {
      setSavingInter(false);
    }
  };

  const Label = ({ children }) => (
    <label className="label-text mb-1 block">{children}</label>
  );

  const Scale1to5 = ({ value, onChange, testId }) => (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = String(value) === String(n);
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(active ? '' : n)}
            className="w-9 h-9 rounded-[10px] text-sm font-semibold transition-colors"
            style={{
              background: active ? 'var(--color-blue-main)' : 'var(--color-bg-elevated)',
              color: active ? '#fff' : 'var(--color-text-secondary)',
              border: `1px solid ${active ? 'var(--color-blue-main)' : 'var(--color-border)'}`,
            }}
          >{n}</button>
        );
      })}
      {value !== '' && value !== null && (
        <button type="button" onClick={() => onChange('')} className="text-xs ml-2 self-center underline" style={{ color: 'var(--color-text-muted)' }}>limpiar</button>
      )}
    </div>
  );

  const YesNo = ({ value, onChange, testId }) => (
    <div className="flex gap-2" data-testid={testId}>
      {[['SI', 'Sí'], ['NO', 'No']].map(([v, l]) => {
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(active ? '' : v)}
            className="px-4 py-2 rounded-[10px] text-sm font-medium transition-colors"
            style={{
              background: active ? 'var(--color-blue-main)' : 'var(--color-bg-elevated)',
              color: active ? '#fff' : 'var(--color-text-secondary)',
              border: `1px solid ${active ? 'var(--color-blue-main)' : 'var(--color-border)'}`,
            }}>{l}</button>
        );
      })}
    </div>
  );

  const TriState = ({ value, onChange, testId }) => (
    <div className="flex gap-2" data-testid={testId}>
      {[['SI', 'Sí'], ['NO', 'No'], ['DUDA', 'Duda']].map(([v, l]) => {
        const active = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(active ? '' : v)}
            className="px-4 py-2 rounded-[10px] text-sm font-medium transition-colors"
            style={{
              background: active ? 'var(--color-blue-main)' : 'var(--color-bg-elevated)',
              color: active ? '#fff' : 'var(--color-text-secondary)',
              border: `1px solid ${active ? 'var(--color-blue-main)' : 'var(--color-border)'}`,
            }}>{l}</button>
        );
      })}
    </div>
  );

  const TipoBadge = ({ tipo }) => {
    const klass = tipo === 'llamada' ? 'badge-blue'
      : tipo === 'mensaje' ? 'badge-success'
      : tipo === 'presencial' ? 'badge-warning'
      : 'badge-neutral';
    return <span className={klass}>{tipo}</span>;
  };

  return (
    <div className="space-y-5" data-testid="mediation-section">
      {/* Estado general */}
      <div className="card-base p-6">
        <h3 className="heading-3 mb-4">Estado de la mediación</h3>
        <div className="max-w-sm">
          <Label>Estado</Label>
          <select className="input-base" value={form.estado_mediacion}
            onChange={(e) => update({ estado_mediacion: e.target.value })} data-testid="med-estado">
            <option value="">—</option>
            {ESTADO_MEDIACION_OPCIONES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
      </div>

      {/* Bloque Interacción — registra una nueva interacción con origen='mediacion' */}
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-3">Interacción</h3>
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Las interacciones registradas aquí aparecen también en la pestaña
            Interacciones marcadas como <span className="badge-blue ml-1">mediación</span>.
          </div>
        </div>

        {/* Auto-computed summary */}
        <div className="grid md:grid-cols-3 gap-4 mb-5">
          <div className="p-3 rounded-[10px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <Label>¿Contactado?</Label>
            <YesNo value={form.contactado} onChange={(v) => update({ contactado: v })} testId="med-contactado" />
          </div>
          <div className="p-3 rounded-[10px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <Label>Número de interacciones <span className="opacity-60">(auto)</span></Label>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }} data-testid="med-numero-contactos">
              {form.numero_contactos ?? 0}
            </div>
          </div>
          <div className="p-3 rounded-[10px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <Label>Fecha última interacción <span className="opacity-60">(auto)</span></Label>
            <div className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }} data-testid="med-fecha-ultimo">
              {form.fecha_ultimo_contacto || '—'}
            </div>
          </div>
        </div>

        <div className="mb-5">
          <Label>Fecha primer contacto</Label>
          <input type="date" className="input-base max-w-sm" value={form.fecha_primer_contacto}
            onChange={(e) => update({ fecha_primer_contacto: e.target.value })} data-testid="med-fecha-primer" />
        </div>

        {/* New interaction form */}
        <div className="p-4 rounded-[10px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Registrar nueva interacción</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Tipo *</Label>
              <select className="input-base" value={inter.tipo} onChange={(e) => updateInter({ tipo: e.target.value })} data-testid="med-int-tipo">
                <option value="llamada">Llamada</option>
                <option value="mensaje">Mensaje</option>
                <option value="email">Email</option>
                <option value="presencial">Presencial</option>
              </select>
            </div>
            <div>
              <Label>Fecha *</Label>
              <input type="date" className="input-base" value={inter.fecha} onChange={(e) => updateInter({ fecha: e.target.value })} data-testid="med-int-fecha" />
            </div>
            <div>
              <Label>Hora</Label>
              <input type="time" className="input-base" value={inter.hora} onChange={(e) => updateInter({ hora: e.target.value })} data-testid="med-int-hora" />
            </div>
            <div>
              <Label>Contacto *</Label>
              <select className="input-base" value={inter.contacto_id}
                onChange={(e) => { updateInter({ contacto_id: e.target.value }); setShowNewContact(e.target.value === 'new'); }}
                data-testid="med-int-contacto">
                <option value="">Seleccionar</option>
                {(contacts || []).map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.tipo ? ` (${c.tipo})` : ''}</option>)}
                <option value="new">+ Nuevo contacto</option>
              </select>
            </div>
          </div>
          {showNewContact && (
            <div className="mt-3 max-w-md">
              <Label>Nombre nuevo contacto</Label>
              <input type="text" className="input-base" value={inter.contacto_nombre}
                onChange={(e) => updateInter({ contacto_nombre: e.target.value })} data-testid="med-int-new-contact" />
            </div>
          )}
          <div className="mt-3">
            <Label>Resumen / comentarios</Label>
            <textarea className="input-base min-h-[60px]" value={inter.resumen} onChange={(e) => updateInter({ resumen: e.target.value })} data-testid="med-int-resumen" />
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={handleRegisterInteraction} disabled={savingInter} className="btn-primary" data-testid="med-register-interaction-btn">
              {savingInter ? 'Registrando…' : '+ Registrar interacción'}
            </button>
          </div>
        </div>

        {/* Listing of interactions registered from mediación */}
        <div className="mt-5">
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Historial de interacciones (desde mediación) · {mediacionInteractions.length}
          </p>
          {mediacionInteractions.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Aún no hay interacciones registradas desde aquí.</p>
          ) : (
            <div className="space-y-2" data-testid="med-int-list">
              {mediacionInteractions.map((i) => (
                <div key={i.id} className="p-3 rounded-[10px]" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <TipoBadge tipo={i.tipo} />
                    <span className="text-sm font-medium">{i.fecha}{i.hora ? ` ${i.hora}` : ''}</span>
                    {i.contacto_nombre && <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>· {i.contacto_nombre}</span>}
                  </div>
                  {i.resumen && <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{i.resumen}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bloque Negociación */}
      <div className="card-base p-6">
        <h3 className="heading-3 mb-4">Negociación</h3>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>Receptividad (1-5)</Label>
            <Scale1to5 value={form.receptividad} onChange={(v) => update({ receptividad: v })} testId="med-receptividad" />
          </div>
          <div>
            <Label>Voluntad de pacto (1-5)</Label>
            <Scale1to5 value={form.voluntad_pacto} onChange={(v) => update({ voluntad_pacto: v })} testId="med-voluntad" />
          </div>
          <div>
            <Label>Capacidad económica (1-5)</Label>
            <Scale1to5 value={form.capacidad_economica} onChange={(v) => update({ capacidad_economica: v })} testId="med-capacidad" />
          </div>
          <div>
            <Label>Probabilidad de acuerdo (0-100)</Label>
            <div className="flex items-center gap-3">
              <input type="range" min="0" max="100" step="5"
                value={form.probabilidad_acuerdo === '' ? 0 : form.probabilidad_acuerdo}
                onChange={(e) => update({ probabilidad_acuerdo: e.target.value })}
                className="flex-1" data-testid="med-probabilidad-range" />
              <input type="number" min="0" max="100"
                value={form.probabilidad_acuerdo}
                onChange={(e) => update({ probabilidad_acuerdo: e.target.value })}
                className="input-base w-20" data-testid="med-probabilidad" />
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>%</span>
            </div>
          </div>
          <div>
            <Label>¿Pide compensación?</Label>
            <YesNo value={form.pide_compensacion}
              onChange={(v) => update({ pide_compensacion: v, importe_compensacion: v === 'NO' ? '' : form.importe_compensacion })}
              testId="med-pide-compensacion" />
          </div>
          <div>
            <Label>Importe compensación (€)</Label>
            <input type="number" min="0" step="100" className="input-base"
              value={form.importe_compensacion}
              onChange={(e) => update({ importe_compensacion: e.target.value })}
              disabled={form.pide_compensacion === 'NO'}
              style={form.pide_compensacion === 'NO' ? { opacity: 0.5 } : {}}
              data-testid="med-importe-compensacion" />
          </div>
          <div>
            <Label>Plazo de pacto (meses)</Label>
            <input type="number" min="0" max="240" className="input-base"
              value={form.plazo_pacto}
              onChange={(e) => update({ plazo_pacto: e.target.value })}
              data-testid="med-plazo" />
          </div>
        </div>
      </div>

      {/* Bloque Ocupación y posesión — clave para el score */}
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading-3">Ocupación y posesión</h3>
          <span className="badge-blue" title="Impacta en el scoring de mediación">clave para el score</span>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label>¿Ocupación confirmada?</Label>
            <TriState
              value={form.ocupacion_confirmada}
              onChange={(v) => update({ ocupacion_confirmada: v })}
              testId="med-ocupacion-confirmada"
            />
          </div>
          <div>
            <Label>Tipo de ocupante actual</Label>
            <select
              className="input-base"
              value={form.tipo_ocupante_actual}
              onChange={(e) => update({ tipo_ocupante_actual: e.target.value })}
              data-testid="med-tipo-ocupante"
            >
              <option value="">—</option>
              {TIPO_OCUPANTE_OPCIONES.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>¿Posesión probable tras adjudicación?</Label>
            <TriState
              value={form.posesion_probable_tras_adjudicacion}
              onChange={(v) => update({ posesion_probable_tras_adjudicacion: v })}
              testId="med-posesion-probable"
            />
          </div>
          <div>
            <Label>Riesgo de lanzamiento (1-5)</Label>
            <Scale1to5
              value={form.riesgo_lanzamiento}
              onChange={(v) => update({ riesgo_lanzamiento: v })}
              testId="med-riesgo-lanzamiento"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              1 = trámite sencillo · 5 = alto riesgo de bloqueo judicial
            </p>
          </div>
          <div>
            <Label>Riesgo de resistencia a la salida (1-5)</Label>
            <Scale1to5
              value={form.riesgo_resistencia_salida}
              onChange={(v) => update({ riesgo_resistencia_salida: v })}
              testId="med-riesgo-resistencia"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              1 = cooperativo · 5 = alta resistencia (activistas, colectivos, menores, vulnerabilidad)
            </p>
          </div>
        </div>
      </div>

      {/* Bloque Notas */}
      <div className="card-base p-6">
        <h3 className="heading-3 mb-4">Notas del mediador</h3>
        <textarea className="input-base" rows={6} value={form.notas_mediador}
          onChange={(e) => update({ notas_mediador: e.target.value })}
          placeholder="Observaciones, contexto, compromisos verbales, próximos pasos…"
          data-testid="med-notas" />
      </div>

      <div className="flex justify-end">
        <button onClick={handleSaveMediation} disabled={saving} className="btn-primary" data-testid="med-save-btn">
          {saving ? 'Guardando…' : 'Guardar mediación'}
        </button>
      </div>
    </div>
  );
};

const InteractionsSection = ({ assetId, interactions, contacts, users, onRefresh, onRefreshContacts }) => {
  const [form, setForm] = useState({ tipo: 'llamada', fecha: '', hora: '', contacto_id: '', contacto_nombre: '', resumen: '' });
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const handleAdd = async () => {
    if (!form.fecha || !form.tipo) { toast.error('Fecha y tipo obligatorios'); return; }
    if (!form.contacto_id && !form.contacto_nombre) { toast.error('Selecciona un contacto'); return; }
    setSaving(true);
    try {
      const p = { ...form, asset_id: assetId };
      if (form.contacto_id && form.contacto_id !== 'new') { const c = contacts.find(x => x.id === form.contacto_id); p.contacto_nombre = c?.nombre || ''; }
      await axios.post(`${API}/assets/${assetId}/interactions`, p, { withCredentials: true });
      setForm({ tipo: 'llamada', fecha: '', hora: '', contacto_id: '', contacto_nombre: '', resumen: '' });
      setShowNew(false); onRefresh(); if (form.contacto_id === 'new') onRefreshContacts();
      toast.success('Interaccion registrada');
    } catch { toast.error('Error'); } finally { setSaving(false); }
  };
  const handleDel = async (iid) => { try { await axios.delete(`${API}/assets/${assetId}/interactions/${iid}`, { withCredentials: true }); onRefresh(); } catch {} };
  return (
    <div className="card-base p-6">
      <h3 className="heading-3 mb-6">Interacciones</h3>
      <div className="p-4 bg-slate-50 rounded-lg mb-6 space-y-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1"><label className="text-sm text-slate-600 font-medium">Tipo *</label>
            <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} className="input-base" data-testid="int-tipo">
              <option value="llamada">Llamada</option><option value="mensaje">Mensaje</option><option value="email">Email</option><option value="presencial">Presencial</option>
            </select></div>
          <div className="space-y-1"><label className="text-sm text-slate-600 font-medium">Fecha *</label><input type="date" className="input-base" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})} data-testid="int-fecha" /></div>
          <div className="space-y-1"><label className="text-sm text-slate-600">Hora</label><input type="time" className="input-base" value={form.hora} onChange={e => setForm({...form, hora: e.target.value})} /></div>
          <div className="space-y-1"><label className="text-sm text-slate-600 font-medium">Contacto *</label>
            <select value={form.contacto_id} onChange={e => { setForm({...form, contacto_id: e.target.value}); setShowNew(e.target.value === 'new'); }} className="input-base" data-testid="int-contacto">
              <option value="">Seleccionar</option>{contacts.map(c=><option key={c.id} value={c.id}>{c.nombre}{c.tipo ? ` (${c.tipo})` : ''}</option>)}<option value="new">+ Nuevo contacto</option>
            </select></div>
        </div>
        {showNew && <LF label="Nombre nuevo contacto" value={form.contacto_nombre} onChange={v => setForm({...form, contacto_nombre: v})} />}
        <div className="space-y-1"><label className="text-sm text-slate-600">Resumen / Comentarios</label><textarea className="input-base min-h-[60px]" value={form.resumen} onChange={e => setForm({...form, resumen: e.target.value})} data-testid="int-resumen" /></div>
        <button onClick={handleAdd} disabled={saving} className="btn-primary mt-2" data-testid="add-int-btn">{saving ? 'Guardando...' : '+ Registrar interaccion'}</button>
      </div>
      {interactions.length === 0 ? <p className="text-slate-500 text-center py-6">No hay interacciones</p> : (
        <div className="space-y-3">{interactions.map(i => (
          <div key={i.id} className="p-4 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={`badge-${i.tipo==='llamada'?'blue':i.tipo==='mensaje'?'success':i.tipo==='presencial'?'warning':'neutral'}`}>{i.tipo}</span>
                <span className="font-medium text-sm">{i.fecha}{i.hora ? ` ${i.hora}` : ''}</span>
              </div>
              <button onClick={() => handleDel(i.id)} className="text-red-400 hover:text-red-600 text-sm">eliminar</button>
            </div>
            {i.contacto_nombre && <p className="text-sm text-slate-700"><strong>Contacto:</strong> {i.contacto_nombre}</p>}
            {i.resumen && <p className="text-sm text-slate-600 mt-1">{i.resumen}</p>}
          </div>
        ))}</div>
      )}
    </div>
  );
};

const LF = ({ label, value, onChange }) => (
  <div className="space-y-1"><label className="text-sm text-slate-600">{label}</label><input className="input-base" value={value||''} onChange={e => onChange(e.target.value)} /></div>
);

const ContactsSection = ({ assetId, contacts, onRefresh, users }) => {
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', tipo: '', tipo_contacto: 'OPERATIVO', notas: '', user_ref_id: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const TIPO_CONTACTO_OPTS = [
    { value: 'OPERATIVO', label: 'Operativo' },
    { value: 'GESTOR', label: 'Gestor' },
    { value: 'INVERSOR', label: 'Inversor' },
  ];

  const TIPO_OPERATIVO_OPTS = ['inmobiliaria', 'abogado', 'procurador', 'registrador', 'deudor', 'administrador', 'agente', 'otro'];

  const handleAdd = async () => {
    if (form.tipo_contacto === 'OPERATIVO' && !form.nombre) { toast.error('Nombre requerido'); return; }
    if ((form.tipo_contacto === 'GESTOR' || form.tipo_contacto === 'INVERSOR') && !form.user_ref_id) { toast.error('Selecciona un usuario'); return; }
    setSaving(true);
    try {
      let payload = { asset_id: assetId, ...form };
      if (form.tipo_contacto !== 'OPERATIVO' && form.user_ref_id) {
        const u = (users || []).find(x => x.id === form.user_ref_id);
        if (u) { payload.nombre = u.name || u.email; payload.email = u.email; }
      }
      await axios.post(`${API}/assets/${assetId}/contacts`, payload, { withCredentials: true });
      setForm({ nombre: '', email: '', telefono: '', tipo: '', tipo_contacto: 'OPERATIVO', notas: '', user_ref_id: '' });
      onRefresh();
      toast.success('Contacto añadido');
    } catch { toast.error('Error al añadir'); } finally { setSaving(false); }
  };

  const handleDelete = async (cid) => {
    try { await axios.delete(`${API}/assets/${assetId}/contacts/${cid}`, { withCredentials: true }); onRefresh(); toast.success('Eliminado'); } catch { toast.error('Error'); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ nombre: c.nombre || '', email: c.email || '', telefono: c.telefono || '', tipo: c.tipo || '', tipo_contacto: c.tipo_contacto || 'OPERATIVO', notas: c.notas || '', user_ref_id: c.user_ref_id || '' });
  };

  const saveEditContact = async (cid) => {
    try {
      await axios.put(`${API}/assets/${assetId}/contacts/${cid}`, { asset_id: assetId, ...editForm }, { withCredentials: true });
      setEditingId(null);
      onRefresh();
      toast.success('Contacto actualizado');
    } catch { toast.error('Error al guardar'); }
  };

  const badgeColor = (tc) => {
    if (tc === 'GESTOR') return 'bg-blue-100 text-blue-700';
    if (tc === 'INVERSOR') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="card-base p-6">
      <h3 className="heading-3 mb-6 flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />Contactos</h3>

      {/* Add form */}
      <div className="p-4 bg-slate-50 rounded-lg mb-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Tipo de contacto *</label>
            <select value={form.tipo_contacto} onChange={e => setForm({...form, tipo_contacto: e.target.value, user_ref_id: '', nombre: '', email: ''})} className="input-base" data-testid="contact-tipo-contacto">
              {TIPO_CONTACTO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {form.tipo_contacto === 'OPERATIVO' ? (
            <>
              <div className="space-y-1">
                <label className="text-sm text-slate-600 font-medium">Nombre *</label>
                <input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} className="input-base" data-testid="contact-nombre" />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-slate-600">Rol / Tipo</label>
                <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} className="input-base" data-testid="contact-tipo">
                  <option value="">Seleccionar...</option>
                  {TIPO_OPERATIVO_OPTS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
            </>
          ) : (
            <div className="space-y-1 md:col-span-2">
              <label className="text-sm text-slate-600 font-medium">Usuario de la plataforma *</label>
              <select value={form.user_ref_id} onChange={e => setForm({...form, user_ref_id: e.target.value})} className="input-base" data-testid="contact-user-ref">
                <option value="">Seleccionar usuario...</option>
                {(users || []).map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>)}
              </select>
            </div>
          )}
        </div>
        {form.tipo_contacto === 'OPERATIVO' && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1"><label className="text-sm text-slate-600">Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input-base" data-testid="contact-email" /></div>
            <div className="space-y-1"><label className="text-sm text-slate-600">Teléfono</label><input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} className="input-base" data-testid="contact-telefono" /></div>
            <div className="space-y-1"><label className="text-sm text-slate-600">Observaciones</label><input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} className="input-base" data-testid="contact-notas" /></div>
          </div>
        )}
        <button onClick={handleAdd} disabled={saving} className="btn-primary" data-testid="add-contact-btn">
          {saving ? 'Guardando...' : <><Plus className="w-4 h-4 mr-2" />Añadir contacto</>}
        </button>
      </div>

      {/* Contact list */}
      {contacts.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No hay contactos</p>
      ) : (
        <div className="space-y-2">
          {contacts.map(c => (
            <div key={c.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`contact-row-${c.id}`}>
              {editingId === c.id ? (
                <div className="space-y-3">
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="space-y-1"><label className="text-xs text-slate-500">Nombre</label><input className="input-base text-sm" value={editForm.nombre} onChange={e => setEditForm({...editForm, nombre: e.target.value})} data-testid="edit-contact-nombre" /></div>
                    <div className="space-y-1"><label className="text-xs text-slate-500">Email</label><input className="input-base text-sm" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} /></div>
                    <div className="space-y-1"><label className="text-xs text-slate-500">Teléfono</label><input className="input-base text-sm" value={editForm.telefono} onChange={e => setEditForm({...editForm, telefono: e.target.value})} /></div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="space-y-1"><label className="text-xs text-slate-500">Tipo contacto</label>
                      <select className="input-base text-sm" value={editForm.tipo_contacto} onChange={e => setEditForm({...editForm, tipo_contacto: e.target.value})}>
                        {TIPO_CONTACTO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1"><label className="text-xs text-slate-500">Rol</label>
                      <select className="input-base text-sm" value={editForm.tipo} onChange={e => setEditForm({...editForm, tipo: e.target.value})}>
                        <option value="">-</option>
                        {TIPO_OPERATIVO_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1"><label className="text-xs text-slate-500">Observaciones</label><input className="input-base text-sm" value={editForm.notas} onChange={e => setEditForm({...editForm, notas: e.target.value})} /></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveEditContact(c.id)} className="btn-primary text-sm px-3 py-1.5" data-testid="save-edit-contact-btn"><Save className="w-3.5 h-3.5 mr-1" />Guardar</button>
                    <button onClick={() => setEditingId(null)} className="btn-secondary text-sm px-3 py-1.5">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{c.nombre}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeColor(c.tipo_contacto)}`}>{c.tipo_contacto || 'OPERATIVO'}</span>
                      {c.tipo && <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{c.tipo}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0 text-sm text-slate-500">
                      {c.email && <span>{c.email}</span>}
                      {c.telefono && <span>{c.telefono}</span>}
                    </div>
                    {c.notas && <p className="text-xs text-slate-400">{c.notas}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button onClick={() => startEdit(c)} className="text-slate-400 hover:text-blue-600 p-1" title="Editar" data-testid={`edit-contact-${c.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(c.id)} className="text-slate-400 hover:text-red-600 p-1" title="Eliminar" data-testid={`delete-contact-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Documents Section
const DOC_CATEGORIES = [
  { value: 'estudio_economico', label: 'Estudio económico' },
  { value: 'documentacion_vivienda', label: 'Documentación vivienda' },
  { value: 'adjudicacion_cargas', label: 'Adjudicación y cancelación de cargas' },
  { value: 'suministros_impuestos', label: 'Suministros, impuestos y seguros' },
  { value: 'reforma_adecuacion', label: 'Reforma y adecuación' },
  { value: 'comercializacion_venta', label: 'Comercialización y venta' },
  { value: 'balance_facturas', label: 'Balance económico y facturas' },
  { value: 'fotos_audios', label: 'Fotos y audios' },
  { value: 'otros', label: 'Otros' },
];

const DOC_TIPOS = [
  { value: 'documento', label: 'Documento' },
  { value: 'factura', label: 'Factura (contabilidad)' },
];

const DOC_GASTO_CATS = [
  { value: 'balance_facturas', label: 'Facturas' },
  { value: 'impuestos', label: 'Impuestos' },
  { value: 'registral', label: 'Registral' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'comercializacion', label: 'Comercialización' },
  { value: 'posesion', label: 'Posesión' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'otros', label: 'Otros' },
];

const DocumentsSection = ({ assetId, documents, onRefresh, readOnly, canEdit, canDelete = true, canDownload = true, canPreview = true }) => {
  const [uploading, setUploading] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [categoria, setCategoria] = useState('otros');
  const [comentario, setComentario] = useState('');
  const [activeFolder, setActiveFolder] = useState(null);
  const fileInputRef = React.useRef(null);
  const batchInputRef = React.useRef(null);
  const folderInputRef = React.useRef(null);

  // Batch upload staging state
  const [batchStaging, setBatchStaging] = useState([]); // [{file, categoria, tipo_documento, comentario, status}]
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

  // Invoice extraction state
  const [extractingDocId, setExtractingDocId] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({});
  const [confirmingInvoice, setConfirmingInvoice] = useState(false);
  const [creatingGasto, setCreatingGasto] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // { id, nombre } when modal is open
  const [previewLoading, setPreviewLoading] = useState(false);

  // Inline rename state
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const openPreview = async (doc) => {
    // Open the modal immediately with a loader, then fetch the blob.
    setPreviewDoc({ id: doc.id, nombre: doc.nombre });
    setPreviewLoading(true);
    setPreviewUrl(null);
    setPreviewType(null);
    try {
      // Vista previa con marca de agua visible para TODOS los roles.
      // El endpoint estampa watermark (usuario+email+fecha) en PDFs e imágenes
      // y registra audit log de cada visualización (source=preview).
      const res = await axios.get(`${API}/documents/${doc.id}/print-preview?source=preview`, { withCredentials: true, responseType: 'blob' });
      const ct = res.headers['content-type'] || '';
      const url = window.URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewType(ct.startsWith('image/') ? 'image' : ct.includes('pdf') ? 'pdf' : 'other');
    } catch {
      toast.error('No se pudo abrir el documento');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewType(null);
    setPreviewLoading(false);
  };

  const startRename = (doc) => {
    setEditingDocId(doc.id);
    setEditingName(doc.nombre || '');
  };

  const cancelRename = () => {
    setEditingDocId(null);
    setEditingName('');
  };

  const saveRename = async () => {
    if (!editingDocId) return;
    const trimmed = editingName.trim();
    if (!trimmed) { toast.error('El nombre no puede estar vacío'); return; }
    setSavingName(true);
    try {
      await axios.put(
        `${API}/assets/${assetId}/documents/${editingDocId}`,
        { nombre: trimmed },
        { withCredentials: true },
      );
      toast.success('Nombre actualizado');
      setEditingDocId(null);
      setEditingName('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al renombrar');
    } finally {
      setSavingName(false);
    }
  };

  const handleUploadClick = () => { if (fileInputRef.current) fileInputRef.current.click(); };

  const loadPreview = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/print-preview?source=preview`, { withCredentials: true, responseType: 'blob' });
      const ct = res.headers['content-type'] || '';
      const url = window.URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewType(ct.startsWith('image/') ? 'image' : ct.includes('pdf') ? 'pdf' : 'other');
    } catch {
      setPreviewUrl(null);
      setPreviewType(null);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo_documento', tipoDocumento);
      formData.append('comentario', comentario);
      formData.append('categoria', categoria);
      const res = await axios.post(`${API}/assets/${assetId}/documents`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setTipoDocumento('');
      setComentario('');
      onRefresh();
      toast.success('Documento subido');

      // Auto-trigger extraction if factura in balance_facturas
      if (categoria === 'balance_facturas') {
        triggerExtraction(res.data.id);
      }
      setCategoria('otros');
    } catch { toast.error('Error al subir documento'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // === BATCH UPLOAD ===
  // Guess sensible defaults per file based on extension or filename keywords.
  // IMPORTANT: Values must match DOC_CATEGORIES / DOC_TIPOS so the selects render them.
  const guessDefaults = (filename) => {
    const lower = filename.toLowerCase();
    const isPdf = /\.pdf$/i.test(filename);
    // Invoices / bills
    if (/factur|invoice|recibo|abono/.test(lower)) {
      return { categoria: 'balance_facturas', tipo_documento: 'factura' };
    }
    // Notarial / property deeds
    if (/escritur|notari|titulo|nota_simple|registr/.test(lower)) {
      return { categoria: 'documentacion_vivienda', tipo_documento: 'documento' };
    }
    // Taxes
    if (/impuesto|itp|ajd|iva|modelo_?\d{3}|ibi|plusval/.test(lower)) {
      return { categoria: 'suministros_impuestos', tipo_documento: 'documento' };
    }
    // Legal / auctions
    if (/juridic|abogad|demanda|subasta|decret|sentenc|adjudic/.test(lower)) {
      return { categoria: 'adjudicacion_cargas', tipo_documento: 'documento' };
    }
    // Reform / works
    if (/reforma|obra|presupues|albaran|material/.test(lower)) {
      return { categoria: 'reforma_adecuacion', tipo_documento: 'documento' };
    }
    // Sale / commercialization
    if (/venta|contrato|arras|comercial|oferta/.test(lower)) {
      return { categoria: 'comercializacion_venta', tipo_documento: 'documento' };
    }
    // Photos / audios
    if (/\.(png|jpe?g|webp|heic|heif|gif)$/i.test(filename) || /foto|image|photo|img/.test(lower)) {
      return { categoria: 'fotos_audios', tipo_documento: 'documento' };
    }
    if (/\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(filename) || /audio|voz|grabacion/.test(lower)) {
      return { categoria: 'fotos_audios', tipo_documento: 'documento' };
    }
    return { categoria: 'otros', tipo_documento: isPdf ? 'documento' : '' };
  };

  const onBatchSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const staged = files.map(f => {
      const g = guessDefaults(f.name);
      return { file: f, categoria: g.categoria, tipo_documento: g.tipo_documento, comentario: '', status: 'pendiente' };
    });
    setBatchStaging(staged);
    // Reset input so the same file can be selected again later
    if (batchInputRef.current) batchInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const updateBatchRow = (idx, partial) => {
    setBatchStaging(prev => prev.map((row, i) => i === idx ? { ...row, ...partial } : row));
  };

  const removeBatchRow = (idx) => {
    setBatchStaging(prev => prev.filter((_, i) => i !== idx));
  };

  const applyCategoriaAll = (cat) => {
    setBatchStaging(prev => prev.map(row => ({ ...row, categoria: cat })));
  };

  const applyTipoAll = (tipo) => {
    setBatchStaging(prev => prev.map(row => ({ ...row, tipo_documento: tipo })));
  };

  const uploadOne = async (row, idx) => {
    updateBatchRow(idx, { status: 'subiendo' });
    try {
      const fd = new FormData();
      fd.append('file', row.file);
      fd.append('tipo_documento', row.tipo_documento || '');
      fd.append('categoria', row.categoria || 'otros');
      fd.append('comentario', row.comentario || '');
      const res = await axios.post(`${API}/assets/${assetId}/documents`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      updateBatchRow(idx, { status: 'ok', docId: res.data?.id });
      return { ok: true, row, idx, docId: res.data?.id };
    } catch (err) {
      updateBatchRow(idx, { status: 'error', errorMsg: err.response?.data?.detail || err.message });
      return { ok: false, row, idx };
    }
  };

  const runBatchUpload = async () => {
    if (!batchStaging.length) return;
    const pending = batchStaging.map((r, i) => ({ r, i })).filter(x => x.r.status !== 'ok');
    const total = batchStaging.length;
    const preOk = total - pending.length;
    setBatchProgress({ done: preOk, total });
    let done = preOk;
    let failed = 0;
    const CONCURRENCY = 3;
    const queue = [...pending];

    const worker = async () => {
      while (queue.length) {
        const next = queue.shift();
        if (!next) break;
        const res = await uploadOne(next.r, next.i);
        if (!res.ok) failed++;
        done++;
        setBatchProgress({ done, total });
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));
    onRefresh();
    if (failed === 0) {
      toast.success(`${done} documento${done !== 1 ? 's' : ''} subido${done !== 1 ? 's' : ''}`);
      setTimeout(() => setBatchStaging([]), 800);
    } else {
      toast.error(`${done - failed} OK, ${failed} con errores`);
    }
  };

  const triggerExtraction = async (docId) => {
    setExtractingDocId(docId);
    loadPreview(docId);
    try {
      const res = await axios.post(`${API}/documents/${docId}/extract-invoice`, {}, { withCredentials: true });
      if (res.data.success) {
        setInvoiceModal({ docId, status: 'pendiente_revision' });
        setInvoiceForm(res.data.invoice_data || {});
        onRefresh();
        toast.success('Datos extraídos. Revisa y confirma.');
      }
    } catch (err) {
      // Extraction failed — open modal with empty form for manual entry
      toast.info('No se pudieron extraer datos automáticamente. Rellena manualmente.');
      setInvoiceModal({ docId, status: 'pendiente_revision' });
      setInvoiceForm({ proveedor_emisor: '', cif_emisor: '', numero_factura: '', fecha_factura: '', base_imponible: 0, iva: 0, retencion: 0, total: 0, concepto_resumido: '' });
    } finally { setExtractingDocId(null); }
  };

  const confirmInvoice = async () => {
    if (!invoiceModal) return;
    setConfirmingInvoice(true);
    try {
      await axios.put(`${API}/documents/${invoiceModal.docId}/confirm-invoice`, invoiceForm, { withCredentials: true });
      toast.success('Factura confirmada');
      setInvoiceModal(prev => ({ ...prev, status: 'confirmado' }));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setConfirmingInvoice(false); }
  };

  const createGastoFromInvoice = async () => {
    if (!invoiceModal) return;
    setCreatingGasto(true);
    try {
      await axios.post(`${API}/documents/${invoiceModal.docId}/create-gasto`, {}, { withCredentials: true });
      toast.success('Línea de gasto creada');
      setInvoiceModal(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setCreatingGasto(false); }
  };

  const handleDelete = async (docId) => {
    try { await axios.delete(`${API}/assets/${assetId}/documents/${docId}`, { withCredentials: true }); onRefresh(); toast.success('Eliminado'); } catch { toast.error('Error'); }
  };

  const togglePublic = async (docId) => {
    try {
      const res = await axios.put(`${API}/assets/${assetId}/documents/${docId}/toggle-public`, {}, { withCredentials: true });
      toast.success(res.data.is_public ? 'Documento público para inversores' : 'Documento privado');
      onRefresh();
    } catch { toast.error('Error'); }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', doc.nombre);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Error al descargar'); }
  };

  const openInvoiceData = (doc) => {
    setInvoiceModal({ docId: doc.id, status: doc.invoice_status || 'pendiente_revision' });
    setInvoiceForm(doc.invoice_data || {});
    loadPreview(doc.id);
  };

  const filteredDocs = activeFolder ? documents.filter(d => d.categoria === activeFolder) : documents;
  const catCount = (cat) => documents.filter(d => d.categoria === cat).length;

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const invoiceStatusBadge = (status) => {
    if (status === 'confirmado') return <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Confirmada</span>;
    if (status === 'pendiente_revision') return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Pendiente revisión</span>;
    return null;
  };

  return (
    <div className="card-base p-6">
      <h3 className="heading-3 mb-6 flex items-center gap-2"><FileUp className="w-5 h-5 text-blue-600" />Documentos</h3>

      {/* Upload form */}
      {!readOnly && (
      <div className="p-4 bg-slate-50 rounded-lg mb-6 space-y-4">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Categoría</label>
            <select value={categoria} onChange={e => setCategoria(e.target.value)} className="input-base" data-testid="doc-categoria">
              {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600">Tipo documento</label>
            <select value={tipoDocumento} onChange={e => setTipoDocumento(e.target.value)} className="input-base" data-testid="doc-tipo">
              <option value="">Seleccionar...</option>
              {DOC_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600">Comentario</label>
            <input value={comentario} onChange={e => setComentario(e.target.value)} placeholder="Opcional" className="input-base" data-testid="doc-comentario" />
          </div>
          <div className="flex items-end">
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} data-testid="doc-file-input" />
            <button type="button" onClick={handleUploadClick} disabled={uploading} className="btn-primary w-full" data-testid="doc-upload-btn">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</> : <><FileUp className="w-4 h-4 mr-2" />Subir 1 documento</>}
            </button>
          </div>
        </div>
        {categoria === 'balance_facturas' && (
          <p className="text-xs text-blue-600 flex items-center gap-1"><ScanLine className="w-3.5 h-3.5" /> Al subir una factura en esta categoría, se extraerán automáticamente los datos para su revisión.</p>
        )}

        {/* Batch upload */}
        <div className="pt-3 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-slate-500 flex-1">¿Muchos documentos a la vez? Súbelos por lotes y asigna categoría/tipo en una sola pantalla.</p>
          <input type="file" multiple ref={batchInputRef} className="hidden" onChange={onBatchSelected} data-testid="doc-batch-input" />
          <input type="file" multiple ref={folderInputRef} className="hidden" onChange={onBatchSelected}
                 webkitdirectory="" directory="" data-testid="doc-folder-input" />
          <button type="button" onClick={() => batchInputRef.current?.click()} className="btn-secondary text-sm" data-testid="doc-batch-files-btn">
            <FileUp className="w-4 h-4 mr-1" /> Subir varios archivos
          </button>
          <button type="button" onClick={() => folderInputRef.current?.click()} className="btn-secondary text-sm" data-testid="doc-batch-folder-btn">
            <FolderOpen className="w-4 h-4 mr-1" /> Subir carpeta completa
          </button>
        </div>
      </div>
      )}

      {/* Batch staging table */}
      {batchStaging.length > 0 && !readOnly && (
        <div className="mb-6 border border-blue-200 bg-blue-50/40 rounded-lg" data-testid="doc-batch-staging">
          <div className="p-3 border-b border-blue-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex flex-col">
              <p className="text-sm font-semibold text-slate-800">
                Confirmación previa: {batchStaging.length} archivo{batchStaging.length !== 1 ? 's' : ''}
                <span className="text-xs text-slate-500 font-normal ml-2">
                  ({(batchStaging.reduce((a, b) => a + b.file.size, 0) / (1024 * 1024)).toFixed(2)} MB totales)
                </span>
              </p>
              {batchProgress.total > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-40 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${Math.round((batchProgress.done / batchProgress.total) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500">{batchProgress.done}/{batchProgress.total}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Asignar a todos:</span>
              <select className="input-base text-xs py-1" onChange={e => { if (e.target.value) applyCategoriaAll(e.target.value); e.target.value = ''; }} data-testid="doc-batch-apply-all">
                <option value="">— Categoría —</option>
                {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select className="input-base text-xs py-1" onChange={e => { if (e.target.value) applyTipoAll(e.target.value); e.target.value = ''; }} data-testid="doc-batch-apply-tipo-all">
                <option value="">— Tipo —</option>
                {DOC_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <button onClick={() => setBatchStaging([])} className="text-xs text-slate-500 hover:text-red-500" data-testid="doc-batch-cancel-btn">Cancelar</button>
              <button onClick={runBatchUpload} className="btn-primary text-xs" data-testid="doc-batch-start-btn">
                Subir {batchStaging.length} archivo{batchStaging.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="text-xs text-slate-500 uppercase">
                  <th className="text-left px-3 py-2">Archivo</th>
                  <th className="text-left px-3 py-2 w-32">Categoría</th>
                  <th className="text-left px-3 py-2 w-28">Tipo</th>
                  <th className="text-left px-3 py-2">Comentario</th>
                  <th className="text-left px-3 py-2 w-20">Estado</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {batchStaging.map((row, idx) => (
                  <tr key={idx} className={`border-t border-slate-200 ${row.status === 'ok' ? 'bg-emerald-50/50' : row.status === 'error' ? 'bg-red-50/50' : ''}`} data-testid={`doc-batch-row-${idx}`}>
                    <td className="px-3 py-2 text-xs text-slate-700 truncate max-w-xs" title={row.file.webkitRelativePath || row.file.name}>
                      {row.file.webkitRelativePath || row.file.name}
                      <span className="text-slate-400 ml-1">({(row.file.size / 1024).toFixed(1)} KB)</span>
                    </td>
                    <td className="px-3 py-1">
                      <select value={row.categoria} onChange={e => updateBatchRow(idx, { categoria: e.target.value })} className="input-base text-xs py-1" data-testid={`doc-batch-categoria-${idx}`}>
                        {DOC_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1">
                      <select value={row.tipo_documento} onChange={e => updateBatchRow(idx, { tipo_documento: e.target.value })} className="input-base text-xs py-1" data-testid={`doc-batch-tipo-${idx}`}>
                        <option value="">—</option>
                        {DOC_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1">
                      <input value={row.comentario} onChange={e => updateBatchRow(idx, { comentario: e.target.value })} placeholder="Opcional" className="input-base text-xs py-1" data-testid={`doc-batch-comentario-${idx}`} />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.status === 'pendiente' && <span className="text-slate-500">Pendiente</span>}
                      {row.status === 'subiendo' && <span className="text-blue-600"><Loader2 className="w-3 h-3 inline animate-spin" /> Subiendo</span>}
                      {row.status === 'ok' && <span className="text-emerald-600 font-semibold">OK</span>}
                      {row.status === 'error' && <span className="text-red-600" title={row.errorMsg || ''}>Error</span>}
                    </td>
                    <td className="px-2">
                      {row.status !== 'subiendo' && (
                        <button onClick={() => removeBatchRow(idx)} className="text-slate-400 hover:text-red-500" data-testid={`doc-batch-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 px-3 py-2 border-t border-blue-200 bg-white/40">
            Tipos y categorías pre-asignados según el nombre del archivo. Puedes ajustarlos antes de subir.
          </p>
        </div>
      )}

      {/* Category folders */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="doc-folders">
        <button onClick={() => setActiveFolder(null)} className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${!activeFolder ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`} data-testid="doc-folder-all">
          Todos ({documents.length})
        </button>
        {DOC_CATEGORIES.map(cat => {
          const count = catCount(cat.value);
          if (count === 0 && cat.value !== activeFolder) return null;
          return (
            <button key={cat.value} onClick={() => setActiveFolder(activeFolder === cat.value ? null : cat.value)}
              className={`text-sm px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 ${activeFolder === cat.value ? 'bg-blue-50 border-blue-200 text-blue-700 font-medium' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
              data-testid={`doc-folder-${cat.value}`}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Document list */}
      {filteredDocs.length === 0 ? (
        <p className="text-slate-500 text-center py-8">{activeFolder ? 'No hay documentos en esta categoría' : 'No hay documentos'}</p>
      ) : (
        <div className="space-y-2">
          {filteredDocs.map(doc => (
            <div key={doc.id} className="p-3 border border-slate-200 rounded-lg flex items-center gap-4" data-testid={`doc-row-${doc.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${doc.invoice_status ? 'bg-amber-50' : 'bg-slate-100'}`}>
                {doc.invoice_status ? <Receipt className="w-5 h-5 text-amber-600" /> : <FileText className="w-5 h-5 text-slate-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {editingDocId === doc.id ? (
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename();
                          if (e.key === 'Escape') cancelRename();
                        }}
                        autoFocus
                        disabled={savingName}
                        maxLength={200}
                        className="input-base text-sm flex-1 min-w-0 py-1"
                        data-testid={`rename-input-${doc.id}`}
                      />
                      <button onClick={saveRename} disabled={savingName} className="text-emerald-600 hover:text-emerald-800 p-1 disabled:opacity-50" title="Guardar (Enter)" data-testid={`rename-save-${doc.id}`}>
                        {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button onClick={cancelRename} disabled={savingName} className="text-slate-400 hover:text-slate-600 p-1 disabled:opacity-50" title="Cancelar (Esc)" data-testid={`rename-cancel-${doc.id}`}>
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      {canPreview ? (
                        <button
                          onClick={() => openPreview(doc)}
                          className="flex items-center gap-1 text-slate-700 hover:text-blue-600 group"
                          title="Abrir vista previa"
                          data-testid={`preview-doc-${doc.id}`}
                        >
                          <Search className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                          <p className="font-medium text-sm truncate underline-offset-2 group-hover:underline">{doc.nombre}</p>
                        </button>
                      ) : (
                        <p
                          className="font-medium text-sm truncate text-slate-700"
                          title="Tu rol no permite previsualizar este documento"
                          data-testid={`doc-name-${doc.id}`}
                        >
                          {doc.nombre}
                        </p>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => startRename(doc)}
                          className="text-slate-300 hover:text-blue-600 p-0.5"
                          title="Renombrar"
                          data-testid={`rename-doc-${doc.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {invoiceStatusBadge(doc.invoice_status)}
                      {doc.gasto_created && <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Gasto creado</span>}
                      {doc.is_public && <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Público</span>}
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  {doc.categoria && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{DOC_CATEGORIES.find(c => c.value === doc.categoria)?.label || doc.categoria}</span>}
                  {doc.tipo_documento && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">{doc.tipo_documento === 'factura' ? 'Factura' : doc.tipo_documento === 'documento' ? 'Documento' : doc.tipo_documento}</span>}
                  <span className="text-xs text-slate-400">{(doc.size / 1024).toFixed(1)} KB</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
                  <span>{formatDateTime(doc.created_at)}</span>
                  {doc.uploaded_by && <span>- {doc.uploaded_by}</span>}
                  {doc.confirmed_by && <span>| Confirmado: {doc.confirmed_by}</span>}
                </div>
                {doc.comentario && <p className="text-xs text-slate-500 mt-0.5">{doc.comentario}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {canEdit && doc.categoria === 'balance_facturas' && !doc.invoice_status && (
                  <button onClick={() => triggerExtraction(doc.id)} disabled={extractingDocId === doc.id}
                    className="text-amber-500 hover:text-amber-700 p-1" title="Extraer datos factura" data-testid={`extract-invoice-${doc.id}`}>
                    {extractingDocId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                  </button>
                )}
                {doc.invoice_data && (
                  <button onClick={() => openInvoiceData(doc)} className="text-blue-500 hover:text-blue-700 p-1" title="Ver datos factura" data-testid={`view-invoice-${doc.id}`}>
                    <Receipt className="w-4 h-4" />
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => togglePublic(doc.id)} className={`p-1 ${doc.is_public ? 'text-emerald-500 hover:text-emerald-700' : 'text-slate-300 hover:text-slate-500'}`} title={doc.is_public ? 'Público (click para ocultar)' : 'Privado (click para publicar)'} data-testid={`toggle-public-${doc.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                )}
                {canDownload && (
                  <button onClick={() => handleDownload(doc)} className="text-slate-400 hover:text-blue-600 p-1" title="Descargar" data-testid={`download-doc-${doc.id}`}><Download className="w-4 h-4" /></button>
                )}
                {!readOnly && canDelete && (
                  <button onClick={() => handleDelete(doc.id)} className="text-slate-400 hover:text-red-600 p-1" title="Eliminar" data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal — embedded view of any document (image / PDF / other) */}
      {previewDoc && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="preview-modal-backdrop"
        >
          <div
            className="rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                <p className="font-semibold truncate" style={{ color: 'var(--color-text-primary)' }} data-testid="preview-modal-title">
                  {previewDoc.nombre}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {previewUrl && canDownload && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-blue-600 p-1.5 rounded hover:opacity-80"
                    style={{ color: 'var(--color-text-muted)' }}
                    title="Abrir en nueva pestaña"
                    data-testid="preview-open-newtab"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => {
                    const url = `${API}/documents/${previewDoc.id}/print-preview?source=print`;
                    const win = window.open(url, '_blank', 'noopener');
                    if (!win) {
                      toast.error('Bloqueo de pop-ups: permite ventanas emergentes para imprimir.');
                    }
                  }}
                  className="hover:text-blue-600 p-1.5 rounded hover:opacity-80"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="Imprimir con marca de agua (usuario + email)"
                  data-testid="preview-print-watermark"
                >
                  <Printer className="w-4 h-4" />
                </button>
                {canDownload && (
                  <button
                    onClick={() => { handleDownload(previewDoc); }}
                    className="hover:text-blue-600 p-1.5 rounded hover:opacity-80"
                    style={{ color: 'var(--color-text-muted)' }}
                    title="Descargar"
                    data-testid="preview-download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={closePreview}
                  className="p-1.5 rounded hover:opacity-80"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="Cerrar"
                  data-testid="preview-close"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 flex items-center justify-center overflow-auto" style={{ background: 'var(--color-bg-elevated)' }}>
              {previewLoading && (
                <div className="text-center" style={{ color: 'var(--color-text-muted)' }}>
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm">Cargando documento…</p>
                </div>
              )}
              {!previewLoading && previewUrl && previewType === 'image' && (
                <img
                  src={previewUrl}
                  alt={previewDoc.nombre}
                  className="max-w-full max-h-full object-contain"
                  data-testid="preview-image"
                />
              )}
              {!previewLoading && previewUrl && previewType === 'pdf' && (
                <iframe
                  src={previewUrl}
                  title={previewDoc.nombre}
                  className="w-full h-full border-0 bg-white"
                  data-testid="preview-pdf"
                />
              )}
              {!previewLoading && previewUrl && previewType === 'other' && (
                <div className="p-6 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-3 opacity-30" />
                  <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>Vista previa no disponible para este tipo de archivo.</p>
                  <a href={previewUrl} target="_blank" rel="noreferrer" className="text-blue-500 underline" data-testid="preview-fallback-download">
                    Descargar / abrir en nueva pestaña
                  </a>
                </div>
              )}
              {!previewLoading && !previewUrl && (
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No se pudo cargar la vista previa.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal - Split: Preview left, Form right */}
      {invoiceModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setInvoiceModal(null); if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }} data-testid="invoice-modal-backdrop">
          <div className="rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} onClick={e => e.stopPropagation()} data-testid="invoice-modal">
            {/* Header */}
            <div className="p-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <h3 className="heading-3 flex items-center gap-2"><Receipt className="w-5 h-5 text-amber-600" /> Datos de Factura</h3>
                <div className="flex items-center gap-2">
                  {invoiceStatusBadge(invoiceModal.status)}
                  <button onClick={() => { setInvoiceModal(null); if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }} className="text-slate-400 hover:text-slate-600 p-1"><XIcon className="w-5 h-5" /></button>
                </div>
              </div>
            </div>

            {/* Body - Split */}
            <div className="flex-1 flex min-h-0">
              {/* Left: Document Preview */}
              <div className="w-1/2 border-r border-slate-200 bg-slate-50 flex flex-col" data-testid="invoice-preview-panel">
                <div className="px-4 py-2 border-b border-slate-200 bg-slate-100">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Vista previa del documento</p>
                </div>
                <div className="flex-1 overflow-auto p-2 flex items-center justify-center">
                  {previewUrl && previewType === 'image' && (
                    <img src={previewUrl} alt="Factura" className="max-w-full max-h-full object-contain rounded" data-testid="invoice-preview-image" />
                  )}
                  {previewUrl && previewType === 'pdf' && (
                    <object data={previewUrl} type="application/pdf" className="w-full h-full rounded" data-testid="invoice-preview-pdf">
                      <p className="text-sm text-slate-500 text-center p-4">No se puede mostrar el PDF. <a href={previewUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Abrir en nueva pestaña</a></p>
                    </object>
                  )}
                  {previewUrl && previewType === 'other' && (
                    <div className="text-center p-6">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Vista previa no disponible para este formato</p>
                      <a href={previewUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline mt-2 inline-block">Descargar archivo</a>
                    </div>
                  )}
                  {!previewUrl && (
                    <div className="text-center p-6">
                      <Loader2 className="w-8 h-8 text-slate-300 mx-auto mb-3 animate-spin" />
                      <p className="text-sm text-slate-400">Cargando vista previa...</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Form */}
              <div className="w-1/2 overflow-y-auto p-5 space-y-4" data-testid="invoice-form-panel">
                <div className="grid grid-cols-2 gap-3">
                  <InvField label="Proveedor / Emisor" field="proveedor_emisor" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvField label="CIF / NIF Emisor" field="cif_emisor" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvField label="Nº Factura" field="numero_factura" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvField label="Fecha Factura" field="fecha_factura" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <InvNumField label="Base Imponible" field="base_imponible" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvNumField label="IVA" field="iva" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvNumField label="Retención" field="retencion" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                  <InvNumField label="Total" field="total" form={invoiceForm} setForm={setInvoiceForm} disabled={invoiceModal.status === 'confirmado'} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-sm text-slate-600 font-medium">Concepto / Descripción</label>
                    <textarea className="input-base mt-1 min-h-[60px]" value={invoiceForm.concepto_resumido || ''} onChange={e => setInvoiceForm({...invoiceForm, concepto_resumido: e.target.value})} disabled={invoiceModal.status === 'confirmado'} data-testid="invoice-concepto" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm text-slate-600 font-medium">Categoría de gasto</label>
                    <select className="input-base mt-1" value={invoiceForm.categoria_gasto || 'balance_facturas'} onChange={e => setInvoiceForm({...invoiceForm, categoria_gasto: e.target.value})} disabled={invoiceModal.status === 'confirmado'} data-testid="invoice-categoria-gasto">
                      {DOC_GASTO_CATS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 flex flex-wrap items-center gap-3 flex-shrink-0">
              {invoiceModal.status !== 'confirmado' && (
                <button onClick={confirmInvoice} disabled={confirmingInvoice} className="btn-primary flex items-center gap-2" data-testid="confirm-invoice-btn">
                  {confirmingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Confirmar datos
                </button>
              )}
              {invoiceModal.status === 'confirmado' && (
                <button onClick={createGastoFromInvoice} disabled={creatingGasto} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="create-gasto-from-invoice-btn">
                  {creatingGasto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Crear línea en Balance económico
                </button>
              )}
              <button onClick={() => { setInvoiceModal(null); if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }} className="btn-secondary" data-testid="close-invoice-modal-btn">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InvField = ({ label, field, form, setForm, disabled }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-600 font-medium">{label}</label>
    <input className="input-base text-sm" value={form[field] || ''} onChange={e => setForm({...form, [field]: e.target.value})} disabled={disabled} data-testid={`invoice-${field}`} />
  </div>
);
const InvNumField = ({ label, field, form, setForm, disabled }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-600 font-medium">{label}</label>
    <input type="number" step="0.01" className="input-base text-sm text-right" value={form[field] ?? ''} onChange={e => setForm({...form, [field]: parseFloat(e.target.value) || 0})} disabled={disabled} data-testid={`invoice-${field}`} />
  </div>
);

// ---- Activity log helpers (human-readable formatter) ----
// Map raw audit `action` codes to a friendly Spanish phrase. Each entry is
// either a string ("Creado el activo") or a function that builds the phrase
// from the meta payload to include relevant context (provider, NPL, file…).
const ACTIVITY_FIELD_LABELS = {
  precio_compra: 'Precio de compra',
  precio_compra_es_estimado: 'Precio estimado',
  valor_salida_base: 'Valor de salida',
  valor_salida_individual: 'Valor de salida (individualizado)',
  tasacion: 'Tasación',
  valor_referencia: 'Valor de referencia',
  valor_adjudicacion: 'Valor de adjudicación',
  deuda_total: 'Deuda total',
  deuda_reclamada: 'Deuda reclamada',
  metros: 'Metros cuadrados',
  estado: 'Estado',
  fase: 'Fase',
  prioridad: 'Prioridad',
  tipo_activo_operativo: 'Tipo operativo',
  tipo_activo: 'Tipo de inmueble',
  provincia: 'Provincia',
  municipio: 'Municipio',
  codigo_postal: 'Código postal',
  direccion: 'Dirección',
  zona_barrio: 'Zona / barrio',
  proveedor: 'Proveedor',
  posesion: 'Posesión',
  vivienda_habitual: 'Vivienda habitual',
  descarte_motivos: 'Motivos de descarte',
  precio_m2_zona: 'Precio €/m² zona',
  observaciones: 'Observaciones',
};

const FIELDS_AS_CURRENCY = new Set([
  'precio_compra', 'valor_salida_base', 'valor_salida_individual',
  'tasacion', 'valor_referencia', 'valor_adjudicacion',
  'deuda_total', 'deuda_reclamada', 'precio_m2_zona',
]);

const fmtActivityValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (FIELDS_AS_CURRENCY.has(field) && typeof v === 'number') {
    try { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v); } catch { return String(v); }
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const buildActivityLabel = (ev) => {
  const m = ev.meta || {};
  switch (ev.action) {
    case 'asset.create':
      return { title: 'Activo creado', detail: m.proveedor ? `Proveedor: ${m.proveedor}` : null };
    case 'asset.update': {
      const changes = m.changes || {};
      const fields = Object.keys(changes);
      const bulkSuffix = m.bulk ? ' (cambio masivo)' : '';
      if (!fields.length) return { title: `Activo actualizado${bulkSuffix}`, detail: null };
      // Build a friendly per-field list
      const items = fields.map(f => {
        const label = ACTIVITY_FIELD_LABELS[f] || f.replace(/_/g, ' ');
        const c = changes[f] || {};
        const before = fmtActivityValue(f, c.from);
        const after = fmtActivityValue(f, c.to);
        return { field: f, label, before, after };
      });
      return { title: items.length === 1
        ? `Cambiado: ${items[0].label}${bulkSuffix}`
        : `Cambiados ${items.length} campos${bulkSuffix}`, items };
    }
    case 'asset.trash':
      return { title: 'Activo movido a la papelera', detail: null };
    case 'asset.delete':
      return { title: 'Activo eliminado', detail: null };
    case 'asset.purge':
      return { title: 'Activo eliminado definitivamente', detail: null };
    case 'asset.refresh_valor_base': {
      const v = m.valor_salida_base;
      // meta.valor_salida_base may be a plain number OR a { from, to } pair
      let detail = null;
      if (v != null) {
        if (typeof v === 'object' && ('to' in v || 'from' in v)) {
          if (v.from === v.to) {
            detail = fmtActivityValue('valor_salida_base', v.to);
          } else {
            detail = `${fmtActivityValue('valor_salida_base', v.from)} → ${fmtActivityValue('valor_salida_base', v.to)}`;
          }
        } else {
          detail = fmtActivityValue('valor_salida_base', v);
        }
      }
      return { title: 'Valor de salida recalculado', detail };
    }
    case 'asset.merge.master':
      return { title: 'Recibió duplicados fusionados', detail: null };
    case 'asset.merge.duplicate':
      return { title: 'Fusionado en otro activo', detail: m.master_id ? `Maestro: ${m.master_id.slice(0,8)}…` : null };
    case 'asset.pdf_import':
      return { title: 'Creado desde PDF', detail: m.filename || null };
    case 'asset.bulk_estado':
      return { title: `Estado actualizado masivamente${m.estado ? ` a "${m.estado}"` : ''}`, detail: null };
    case 'asset.bulk_restore':
      return { title: 'Restaurado desde la papelera', detail: null };
    case 'assets.bulk_recalc_scores':
      return { title: 'Scoring recalculado', detail: null };
    case 'catastro.consult':
      return { title: 'Consultado al Catastro', detail: m.status === 'rate_limited' ? 'Límite por hora alcanzado' : (m.status || null) };
    case 'import.excel':
      return { title: 'Importado desde Excel', detail: m.filename || m.proveedor || null };
    case 'import.merge.deuda':
      return { title: 'Nueva deuda añadida desde importación', detail: m.acreedor || null };
    case 'gasto.create':
    case 'balance_gasto.create':
      return { title: 'Línea de gasto añadida', detail: m.concepto ? `${m.concepto}${m.importe != null ? ` · ${fmtActivityValue('precio_compra', m.importe)}` : ''}` : null };
    case 'gasto.update':
    case 'balance_gasto.update':
      return { title: 'Línea de gasto editada', detail: m.concepto || null };
    case 'gasto.delete':
    case 'balance_gasto.delete':
      return { title: 'Línea de gasto eliminada', detail: m.concepto || null };
    case 'document.upload':
      return { title: 'Documento subido', detail: m.nombre || null };
    case 'document.delete':
      return { title: 'Documento eliminado', detail: m.nombre || null };
    default: {
      // Friendly fallback: split the dotted action into Spanish-ish words
      const friendly = ev.action.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return { title: friendly, detail: null };
    }
  }
};

const AssetActivitySection = ({ assetId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState({}); // { [eventId]: true } — toggles JSON details
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/assets/${assetId}/audit-logs`, { withCredentials: true });
        setItems((r.data.items ?? r.data) || []);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, [assetId]);
  if (loading) return <div className="card-base p-6 text-center text-sm text-slate-500">Cargando historial...</div>;
  if (!items.length) return <div className="card-base p-6 text-center text-sm text-slate-500">Sin actividad registrada para este activo.</div>;
  return (
    <div className="card-base p-4" data-testid="asset-activity">
      <h3 className="heading-3 mb-3">Historial de actividad</h3>
      <ul className="divide-y divide-slate-100">
        {items.map(ev => {
          const friendly = buildActivityLabel(ev);
          const expanded = !!showRaw[ev.id];
          return (
            <li key={ev.id} className="py-3 text-sm" data-testid={`activity-${ev.id}`}>
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                <span>{new Date(ev.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full" />
                <span className="font-medium text-slate-600">{ev.user_name || 'sistema'}</span>
              </div>
              <div className="text-slate-800 font-medium">{friendly.title}</div>
              {friendly.detail && (
                <div className="text-xs text-slate-500 mt-0.5">{friendly.detail}</div>
              )}
              {friendly.items && (
                <ul className="mt-2 ml-3 space-y-1">
                  {friendly.items.map((it) => (
                    <li key={it.field} className="text-xs text-slate-600 flex flex-wrap items-baseline gap-1">
                      <span className="font-medium text-slate-700">{it.label}:</span>
                      <span className="text-slate-400 line-through">{it.before}</span>
                      <span className="text-slate-300">→</span>
                      <span className="text-slate-800 font-medium">{it.after}</span>
                    </li>
                  ))}
                </ul>
              )}
              {ev.meta && Object.keys(ev.meta).length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRaw(prev => ({ ...prev, [ev.id]: !prev[ev.id] }))}
                  className="text-[11px] text-slate-400 hover:text-slate-600 mt-1.5 inline-flex items-center gap-1"
                  data-testid={`activity-toggle-${ev.id}`}
                >
                  {expanded ? '− ocultar detalles técnicos' : '+ ver detalles técnicos'}
                </button>
              )}
              {expanded && (
                <pre className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded mt-1 overflow-x-auto">{JSON.stringify(ev.meta, null, 2)}</pre>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AssetDetailPage;

// ---- Multi-deuda panel ----
const DeudasPanel = ({ deudas, formatCurrency, assetId, currentUser, onDeleted }) => {
  const [confirmDeuda, setConfirmDeuda] = useState(null);
  const [deleting, setDeleting] = useState(false);
  if (!Array.isArray(deudas) || deudas.length === 0) return null;
  const totalReclamada = deudas.reduce((s, d) => s + (Number(d.deuda_reclamada) || 0), 0);
  const totalDeuda = deudas.reduce((s, d) => s + (Number(d.deuda_total) || 0), 0);
  const role = currentUser?.role;
  const canDelete = role === 'superadmin' || role === 'admin';
  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('es-ES'); } catch { return iso; }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeuda || !assetId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/assets/${assetId}/deudas/${confirmDeuda.id}`, { withCredentials: true });
      toast.success('Deuda eliminada. Totales recalculados.');
      setConfirmDeuda(null);
      if (onDeleted) onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar la deuda');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card-base p-6 mt-6" data-testid="deudas-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-3">Deudas que gravan el inmueble ({deudas.length})</h3>
        <div className="text-right text-sm">
          <div className="text-slate-500">Suma reclamada: <span className="font-semibold text-slate-900" data-testid="deudas-suma-reclamada">{formatCurrency(totalReclamada)}</span></div>
          <div className="text-slate-500">Suma deuda total: <span className="font-semibold text-slate-900" data-testid="deudas-suma-total">{formatCurrency(totalDeuda)}</span></div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4">#</th>
              <th className="py-2 pr-4">Acreedor</th>
              <th className="py-2 pr-4">Tipo / Rango</th>
              <th className="py-2 pr-4">Nº Préstamo</th>
              <th className="py-2 pr-4 text-right">Deuda total</th>
              <th className="py-2 pr-4 text-right">Deuda reclamada</th>
              <th className="py-2 pr-4">Importado</th>
              {canDelete && <th className="py-2 pr-2 text-right w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {deudas.map((d, i) => (
              <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-2 pr-4 text-slate-500">{i + 1}</td>
                <td className="py-2 pr-4 font-medium">{d.acreedor || '-'}</td>
                <td className="py-2 pr-4">{d.tipo_gravamen || '-'}</td>
                <td className="py-2 pr-4 font-mono text-xs">{d.numero_prestamo || '-'}</td>
                <td className="py-2 pr-4 text-right">{formatCurrency(d.deuda_total)}</td>
                <td className="py-2 pr-4 text-right">{formatCurrency(d.deuda_reclamada)}</td>
                <td className="py-2 pr-4 text-slate-500 text-xs">{fmtDate(d.fecha_importacion)}</td>
                {canDelete && (
                  <td className="py-2 pr-2 text-right">
                    <button
                      type="button"
                      onClick={() => setConfirmDeuda(d)}
                      disabled={!d.id}
                      title={d.id ? 'Eliminar deuda' : 'Esta deuda no tiene ID (no eliminable)'}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      data-testid={`delete-deuda-btn-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de confirmación */}
      {confirmDeuda && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="delete-deuda-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">Eliminar deuda</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Vas a eliminar la deuda de <span className="font-semibold">{confirmDeuda.acreedor || 'acreedor desconocido'}</span>
                    {confirmDeuda.numero_prestamo ? <> (Nº préstamo <span className="font-mono">{confirmDeuda.numero_prestamo}</span>)</> : null}.
                    <br />
                    Los totales del activo (suma reclamada y suma total) se recalcularán automáticamente.
                    <br />
                    <span className="text-red-600 font-medium">Esta acción no se puede deshacer.</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 rounded-b-lg flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDeuda(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
                data-testid="delete-deuda-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="delete-deuda-confirm"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Eliminar deuda
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Participantes panel (read-only en Resumen) ----
const ParticipantesPanel = ({ participantes, formatCurrency }) => {
  const parts = (participantes || []).filter(p => (Number(p.aportacion) || 0) > 0);
  if (parts.length === 0) return null;
  const total = parts.reduce((s, p) => s + (Number(p.aportacion) || 0), 0);
  return (
    <div className="card-base p-6 mt-6" data-testid="participantes-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className="heading-3">Participación en beneficios ({parts.length})</h3>
        <div className="text-right text-sm">
          <span className="text-slate-500">Aportación total: </span>
          <span className="font-semibold text-slate-900">{formatCurrency(total)}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4">#</th>
              <th className="py-2 pr-4">Rol</th>
              <th className="py-2 pr-4">Participante</th>
              <th className="py-2 pr-4 text-right">Aportación</th>
              <th className="py-2 pr-4 text-right">% Participación</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p, i) => {
              const pct = total > 0 ? ((Number(p.aportacion) || 0) / total * 100) : 0;
              const rolLabel = p.rol === 'gestor' ? 'Gestor' : p.rol === 'inversor' ? 'Inversor' : 'Otro';
              const rolColor = p.rol === 'gestor' ? 'bg-blue-100 text-blue-700' : p.rol === 'inversor' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
              return (
                <tr key={p.id || i} className="border-b border-slate-100">
                  <td className="py-2 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-2 pr-4"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rolColor}`}>{rolLabel}</span></td>
                  <td className="py-2 pr-4 font-medium">{p.nombre || 'Anónimo'}{!p.user_id && <span className="text-xs text-slate-400 ml-2">(externo)</span>}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{formatCurrency(p.aportacion)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums font-semibold">{pct.toFixed(2)}%</td>
                </tr>
              );
            })}
            <tr className="bg-slate-50 font-semibold">
              <td className="py-2 pr-4" colSpan={3}>TOTAL</td>
              <td className="py-2 pr-4 text-right">{formatCurrency(total)}</td>
              <td className="py-2 pr-4 text-right">100,00%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
